import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Alert, Button, Typography } from '@mui/material';
import { DataGrid, gridNumberComparator, useGridApiRef } from '@mui/x-data-grid';
import { BackdropLoader, Icon, palette, SimpleModal, toast } from '@livable/saas-react-components';
import { reaction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useOldServicePeriodConfirm } from 'src/components/allocations/OldServicePeriod';
import { track, events } from 'src/configs/amplitude';
import { ACKNOWLEDGEMENT_REQUIRED, DRAFT_CHANGED } from 'src/constants/submeterBilling';
import { useStore } from 'src/stores';
import useMeterDetailsStyles from './styles';
import noDataSvg from 'src/img/no-data.svg';
import { toSaveRows, toUpdateRows, toRecalculateRequest, findDivergentMeter, toInitialMeterRate, toOverrideRate, multiplyToCents, toMeasure, resolveRateEdit, formatMeterRate, formatMoney, findUnitsMissingReads, findUnitsWithEndBelowStart, findUnitsWithNegativeReads, findUnitsWithReadsAboveMax, isReadInvalid, findUnitsWithUsageMismatch, toStoredRead, toUsageFromReads, formatUsage, haveReadsChanged, isReadEmpty, isSameRead, isUsageUnpriced } from './toSaveRows';
import { VarianceReview } from './VarianceReview';
import { OldUtilityConfigurationModal } from 'src/pages/NewBillingModule/OldUtilityConfigurationModal';
import { getColumnMapVersion, subscribeToColumnMapChanges } from 'src/pages/NewBillingModule/OldUtilityConfigurationModal/columnMapVersion';
import { ParseWarnings } from './ParseWarnings';
import { toProration, toSplitRowShares } from './splitRowDisplay';
import { toBalancedMeterKeys, toMeterTotalMismatches } from './sharedMeterTotals';
import { MeterTotals } from './MeterTotals';
import { findReadsUnits, isBillUsageUnitMismatch } from './billUsageUnit';
import { ReadsUploadAreas } from './ReadsUploadAreas';
import { splitCentsKeepingOwnerDays, toPeriodDays } from './ownerDaysPreview';
import { applyOpenings, findNotLastMonthWarning, findPendingOpenings, findStartAfterEndRows, findUnitConflictRows, hasDuplicateMeters, isSingleReadingTemplate, looksSingleReading, toBaselineMonth, toTemplateKey, toEndRow, toOpeningKey, toOpeningWarnings, toSourceLabel, toTypedOpening, toUploadedOpening, withoutOpeningWarnings } from './singleReading';

// A row with one of these codes is dropped from the parse, so each warning with one counts as a skipped row.
const UNMATCHED_PARSE_CODES = ['unrecognized_meter_id', 'unit_name_not_mapped', 'meter_id_not_mapped', 'meter_id_on_several_units', 'unit_meter_conflict', 'unit_name_on_several_units'];
const SKIPPED_PARSE_CODES = [...UNMATCHED_PARSE_CODES, 'invalid_value'];
const toRefusedFileMessage = label => "The ".concat(label, " file was not used because it has more than one row for the same meter. Fix the file and upload it again.");
// How long Save holds its lock for the reload of the saved rows once the write has landed. A normal reload takes a second
// or two; past this the screen lets go of the save lock rather than hold it for good, and tells the PM the rows did not
// load in time. The reload itself is not cancelled: it is applied whenever it answers, unless something newer has
// superseded it (a new upload, another Save, or any edit to the upload grid), in which case it is ignored whenever it
// answers, inside this bound or after it.
export const SAVE_RELOAD_TIMEOUT_MS = 20000;
const NO_BILL_NOTICE = 'This cycle has no bill. Add it on Bill details.';
const NO_BILL_EMPTY_STATE = 'Meter details appear once the cycle has a bill.';
const NO_BILL_SUBTITLE = 'Meter reads can be entered once the cycle has a bill.';

// Halves round away from zero, matching the backend's MidpointRounding.AwayFromZero. Math.round alone
// rounds a half toward positive infinity, which disagrees on a negative amount: -1.5 becomes -1, not -2.
// `|| 0` turns the -0 a small negative rounds to back into 0, which the grid would otherwise print as "-0".
const roundHalfAway = v => Math.sign(v) * Math.round(Math.abs(v)) || 0;
const round2 = v => Math.sign(v) * Math.round((Math.abs(v) + Number.EPSILON) * 100) / 100 || 0;
const num = v => Number(v) || 0;

// Module level, so a rebuild still counts as running after its screen unmounts and mounts again.
const rebuildsInFlight = new Set();
// Row writes (Save's PUT, the upload save) per cycle, so a screen opened during one waits for it before reading rows.
const rowWritesInFlight = new Map();
// Approvals per cycle from the start of their save to the end of their approve request, which row writes alone do not span.
const approvalsInFlight = new Set();
const cycleWorkListeners = new Set();
const notifyCycleWork = () => cycleWorkListeners.forEach(listener => listener());
const setRebuildInFlight = (key, isRunning) => {
  if (isRunning) rebuildsInFlight.add(key);else rebuildsInFlight.delete(key);
  notifyCycleWork();
};
const trackRowWrite = async (key, write) => {
  var _rowWritesInFlight$ge;
  rowWritesInFlight.set(key, ((_rowWritesInFlight$ge = rowWritesInFlight.get(key)) !== null && _rowWritesInFlight$ge !== void 0 ? _rowWritesInFlight$ge : 0) + 1);
  notifyCycleWork();
  try {
    const result = await write();
    return result;
  } finally {
    const count = rowWritesInFlight.get(key) - 1;
    if (count > 0) rowWritesInFlight.set(key, count);else rowWritesInFlight.delete(key);
    notifyCycleWork();
  }
};
const trackApproval = (key, approve) => {
  approvalsInFlight.add(key);
  return approve().finally(() => approvalsInFlight.delete(key));
};
const subscribeToCycleWork = listener => {
  cycleWorkListeners.add(listener);
  return () => cycleWorkListeners.delete(listener);
};
const toastFailure = asyncStore => {
  var _error$errorResp;
  const error = asyncStore.error;
  toast('error', (_error$errorResp = error === null || error === void 0 ? void 0 : error.errorResp) !== null && _error$errorResp !== void 0 ? _error$errorResp : {
    title: 'Error',
    body: [(error === null || error === void 0 ? void 0 : error.message) || 'Error']
  });
};
const USAGE_SCALE = 10000;
const toUsageUnits = v => Math.round(num(v) * USAGE_SCALE);
const formatUsageUnits = units => (units / USAGE_SCALE).toLocaleString('en-US', {
  maximumFractionDigits: 4
});
export const sumMeteredUsage = (rows, countEachMeterOnce) => {
  const seen = new Set();
  return rows.reduce((sum, row) => {
    if (countEachMeterOnce) {
      var _row$meterSerialNumbe;
      const key = "".concat(row.unitId, "::").concat((_row$meterSerialNumbe = row.meterSerialNumber) !== null && _row$meterSerialNumbe !== void 0 ? _row$meterSerialNumbe : '');
      if (seen.has(key)) return sum;
      seen.add(key);
    }
    return sum + toUsageUnits(row.meterUsage);
  }, 0);
};

/*
 * An exact copy of the backend share splitter's arithmetic, which runs in .NET decimal (a 96-bit
 * integer mantissa and a scale of at most 28), reproduced with BigInt because a float product can
 * land exactly on a half cent where the decimal one falls just short of it. Each step rounds where
 * decimal does: the day share to 28 decimal places, the total times the share to whatever still fits
 * in 96 bits (both half to even), then the amount to the cent, half away from zero.
 */
const DECIMAL_MAX_SCALE = 28;

// Days arrive as whatever the grid holds. The backend stores them as whole days, and BigInt throws on
// a fraction, so anything else counts as its whole part and a negative or non-number counts as none.
const toWholeDays = d => Number.isFinite(d) && d > 0 ? Math.floor(d) : 0;
export const splitCentsByDays = (totalCents, rawDays) => {
  const days = rawDays.map(toWholeDays);
  const totalDays = days.reduce((sum, d) => sum + d, 0);
  if (totalDays <= 0) return days.map(() => 0);
  const safeTotalCents = Number.isFinite(totalCents) ? totalCents : 0;
  if (days.length === 1) return [safeTotalCents];

  // Built here rather than at module scope, so importing this file never needs BigInt: a browser
  // without it fails this split, not the whole app.
  const zero = BigInt(0);
  const one = BigInt(1);
  const two = BigInt(2);
  const mantissaLimit = two ** BigInt(96);
  const pow10 = n => BigInt(10) ** BigInt(n);
  const bigAbs = n => n < zero ? -n : n;
  const divideRounded = (n, d, halfToEven) => {
    let q = n / d;
    const twiceRemainder = two * bigAbs(n % d);
    if (twiceRemainder > d || twiceRemainder === d && (!halfToEven || q % two !== zero)) {
      q += n < zero ? -one : one;
    }
    return q;
  };
  const total = BigInt(safeTotalCents);
  const cents = days.map(d => {
    const share = divideRounded(BigInt(d) * pow10(DECIMAL_MAX_SCALE), BigInt(totalDays), true);
    const product = total * share;
    const productScale = 2 + DECIMAL_MAX_SCALE;
    let drop = productScale - DECIMAL_MAX_SCALE;
    while (bigAbs(divideRounded(product, pow10(drop), true)) >= mantissaLimit) drop += 1;
    const amount = divideRounded(product, pow10(drop), true);
    return divideRounded(amount, pow10(productScale - drop - 2), false);
  });
  const amounts = cents.map(Number);
  const remainder = safeTotalCents - amounts.reduce((sum, c) => sum + c, 0);
  const step = remainder < 0 ? -1 : 1;
  days.map((_, idx) => idx).sort((a, b) => days[b] - days[a]).filter(idx => Math.sign(amounts[idx] + step) !== -Math.sign(safeTotalCents)).slice(0, Math.abs(remainder)).forEach(idx => {
    amounts[idx] += step;
  });
  return amounts;
};

/**
 * Whether the edit changed anything a row's charges are priced from: the rate, the base charge or a
 * read. Usage is not listed: it is read only and changes only through the reads, so a Usage change
 * always arrives with a read change, which counts on its own (a number comparison of Usage could not
 * see an empty Usage going to 0 or a new Usage equal to the old one).
 */
const havePricingInputsChanged = (newRow, oldRow) => ['meterRate', 'baseCharge'].some(k => num(newRow[k]) !== num(oldRow[k])) || haveReadsChanged(newRow, oldRow);

/**
 * A saved row's meterRate, usageCharge and allocationCharge are three independent fields on the
 * wire. Editing one directly must not leave the other two stale, but a directly typed usageCharge
 * or allocationCharge must also survive — so this derives only what the diff between newRow and
 * oldRow implies changed, rather than recomputing every field on every save. Single-residency
 * only: a meter shared by more than one residency goes through deriveMeterGroupCharges below.
 */
export const deriveCharges = (newRow, oldRow) => {
  if (havePricingInputsChanged(newRow, oldRow)) {
    if (isUsageUnpriced(newRow)) return _objectSpread(_objectSpread({}, newRow), {}, {
      usageCharge: '',
      allocationCharge: ''
    });
    const usageCharge = multiplyToCents(toMeasure(num(newRow.meterUsage)), num(newRow.meterRate));
    return _objectSpread(_objectSpread({}, newRow), {}, {
      usageCharge,
      allocationCharge: round2(num(newRow.baseCharge) + usageCharge)
    });
  }
  if (num(newRow.usageCharge) !== num(oldRow.usageCharge)) {
    return _objectSpread(_objectSpread({}, newRow), {}, {
      allocationCharge: round2(num(newRow.baseCharge) + num(newRow.usageCharge))
    });
  }
  return newRow;
};
const idKey = v => String(v !== null && v !== void 0 ? v : '').toLowerCase();
const compareIds = (a, b) => {
  if (a === b) return 0;
  return a < b ? -1 : 1;
};
const byResidencyThenRow = (a, b) => compareIds(idKey(a.residencyId), idKey(b.residencyId)) || compareIds(idKey(a.allocationId), idKey(b.allocationId));

/**
 * A saved row is per residency, but a meter shared by more than one residency (a mid-cycle
 * move-out/move-in on the same unit) stores baseCharge and usageCharge — the meter's own total —
 * duplicated on every one of those rows; only allocationCharge differs, split by
 * serviceDaysInPeriod. deriveCharges alone sets allocationCharge = base + usage on just the edited
 * row, which bills the whole meter to that one resident, leaves siblings on their old values, and
 * leaves the group with mismatched rates the backend refuses on the next edit. On a rate or
 * base edit, a read change, or a directly typed usage charge, this applies the edit and the recomputed total to
 * every row sharing the same meter (groupRows), and splits the total across them by
 * serviceDaysInPeriod. The split is the backend's own share splitter (splitCentsByDays above), which
 * the save path and the bill re-price path both use: each share rounds to the cent, the
 * remainder goes a cent at a time to the longest stays, and a group with no service days gets zero
 * on every row. A change to this split has to land on both sides
 * together. The backend re-price still splits its own total, built from the bill's full-precision
 * rate and base charge rather than the rounded values on these rows, so it can move a cent. A
 * single-row group, or a directly typed total charge (which deriveCharges already keeps row-local),
 * reduces to deriveCharges' own single-row result. An edit to either read also copies both the
 * start and end read to every row of the meter, because the backend refuses the next bill edit
 * when the rows of one meter disagree on them. With the bill's periodDays, the days of the period no row
 * covers stay with the owner, as on Save (see ownerDaysPreview), for a single row meter too.
 */
export const deriveMeterGroupCharges = function (newRow, oldRow, groupRows) {
  var _derived$overrideRate, _oldRow$overrideRate, _splitCentsKeepingOwn2;
  let _ref = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {},
    periodDays = _ref.periodDays;
  const derived = deriveCharges(newRow, oldRow);
  const inputChanged = havePricingInputsChanged(newRow, oldRow);
  const usageChargeTyped = !inputChanged && num(newRow.usageCharge) !== num(oldRow.usageCharge);
  const overrideRate = (_derived$overrideRate = derived.overrideRate) !== null && _derived$overrideRate !== void 0 ? _derived$overrideRate : null;
  const overrideChanged = overrideRate !== ((_oldRow$overrideRate = oldRow.overrideRate) !== null && _oldRow$overrideRate !== void 0 ? _oldRow$overrideRate : null);
  const readsChanged = haveReadsChanged(newRow, oldRow);
  const reads = {
    startMeterRead: derived.startMeterRead,
    endMeterRead: derived.endMeterRead
  };
  const meterGroup = groupRows && groupRows.length > 1 ? groupRows : [newRow];
  // New Service days on a priced meter re-split the whole meter only while the owner still keeps days of the
  // period. Once the rows cover it, Save keeps the stored amounts, so the grid keeps them too.
  const daysAfterEdit = meterGroup.map(r => num((r.id === newRow.id ? derived : r).serviceDaysInPeriod));
  const daysResplit = Number.isFinite(periodDays) && periodDays > 0 && num(newRow.serviceDaysInPeriod) !== num(oldRow.serviceDaysInPeriod) && !isUsageUnpriced(derived) && splitCentsKeepingOwnerDays(0, daysAfterEdit, periodDays) !== null;
  const repriced = inputChanged || usageChargeTyped || daysResplit;
  if (meterGroup.length <= 1) {
    var _splitCentsKeepingOwn;
    if (!repriced || inputChanged && isUsageUnpriced(derived)) return [derived];
    const totalCents = roundHalfAway(round2(num(derived.baseCharge) + num(derived.usageCharge)) * 100);
    const _ref2 = (_splitCentsKeepingOwn = splitCentsKeepingOwnerDays(totalCents, [num(derived.serviceDaysInPeriod)], periodDays)) !== null && _splitCentsKeepingOwn !== void 0 ? _splitCentsKeepingOwn : [],
      _ref3 = _slicedToArray(_ref2, 1),
      ownCents = _ref3[0];
    if (ownCents !== undefined) return [_objectSpread(_objectSpread({}, derived), {}, {
      allocationCharge: ownCents / 100
    })];
    return [derived];
  }
  const meterRows = meterGroup.map(r => r.id === newRow.id ? derived : r);
  if (!repriced) {
    if (!overrideChanged && !readsChanged) return [derived];
    return meterRows.map(r => _objectSpread(_objectSpread(_objectSpread({}, r), overrideChanged && {
      overrideRate
    }), readsChanged && reads));
  }
  if (inputChanged && isUsageUnpriced(derived)) {
    return meterRows.map(r => _objectSpread(_objectSpread({}, r), {}, {
      meterRate: derived.meterRate,
      baseCharge: derived.baseCharge,
      usageCharge: '',
      allocationCharge: '',
      overrideRate
    }, readsChanged && _objectSpread(_objectSpread({}, reads), {}, {
      meterUsage: derived.meterUsage
    })));
  }
  const total = round2(num(derived.baseCharge) + num(derived.usageCharge));
  const ordered = meterRows.map((row, idx) => ({
    row,
    idx
  })).sort((a, b) => byResidencyThenRow(a.row, b.row));
  const rowDays = ordered.map(_ref4 => {
    let row = _ref4.row;
    return num(row.serviceDaysInPeriod);
  });
  const totalCents = roundHalfAway(total * 100);
  const cents = (_splitCentsKeepingOwn2 = splitCentsKeepingOwnerDays(totalCents, rowDays, periodDays)) !== null && _splitCentsKeepingOwn2 !== void 0 ? _splitCentsKeepingOwn2 : splitCentsByDays(totalCents, rowDays);
  const shares = [];
  ordered.forEach((_ref5, pos) => {
    let idx = _ref5.idx;
    shares[idx] = cents[pos] / 100;
  });
  return meterRows.map((r, idx) => _objectSpread(_objectSpread({}, r), {}, {
    meterRate: derived.meterRate,
    baseCharge: derived.baseCharge,
    usageCharge: derived.usageCharge,
    allocationCharge: shares[idx],
    overrideRate
  }, readsChanged && _objectSpread(_objectSpread({}, reads), {}, {
    meterUsage: derived.meterUsage
  })));
};
export const MeterDetails = observer(props => {
  var _keptUpload$templateK, _store$account, _utility$bill, _utility$bill2, _utility$bill3, _currentCompany$billi, _currentCompany$billi2, _store$homeDashboardS5, _store$homeDashboardS6, _store$homeDashboardS7, _parseCsvResponse$bas, _startFile$warnings, _parseCsvResponse$row, _parseCsvResponse$row2, _unmappedUnits$filter, _utility$bill6, _utility$bill8;
  const propertyId = props.propertyId,
    utilityId = props.utilityId,
    billingCycleId = props.billingCycleId,
    billUsage = props.billUsage,
    utility = props.utility,
    onRowsSaved = props.onRowsSaved,
    onNoRows = props.onNoRows,
    onBusy = props.onBusy,
    onApproved = props.onApproved,
    onCleared = props.onCleared,
    servicePeriod = props.servicePeriod,
    _props$unsavedUpload = props.unsavedUpload,
    unsavedUpload = _props$unsavedUpload === void 0 ? null : _props$unsavedUpload,
    onUnsavedUploadChange = props.onUnsavedUploadChange,
    onUnsavedUploadSaved = props.onUnsavedUploadSaved;
  const classes = useMeterDetailsStyles();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const store = useStore();
  const oldServicePeriod = useOldServicePeriodConfirm();
  // The bill-removed effect below closes its dialog; a ref keeps that effect keyed on the bill alone.
  const closeOldServicePeriodRef = useRef(oldServicePeriod.close);
  closeOldServicePeriodRef.current = oldServicePeriod.close;
  const fileInputRef = useRef(null);
  const startFileInputRef = useRef(null);
  const page = 'Apply Billing';
  const propertyUtilityData = store.properties.propertyUtilities.find(u => u.utilityId === utilityId);
  const propertyUtilityId = propertyUtilityData === null || propertyUtilityData === void 0 ? void 0 : propertyUtilityData.propertyUtilityId;

  // Store actions cancel each other's in-flight calls, so a closed screen must not start new reads.
  const isActiveRef = useRef(true);
  useEffect(() => () => {
    isActiveRef.current = false;
  }, []);
  const getSelectedPropertyId = () => {
    var _store$homeDashboardS;
    return (_store$homeDashboardS = store.homeDashboardStore.getCurrentProperty) === null || _store$homeDashboardS === void 0 ? void 0 : _store$homeDashboardS.id;
  };
  const selectedPropertyId = getSelectedPropertyId();
  const selectionRef = useRef({
    id: selectedPropertyId,
    generation: 0
  });
  const advanceSelection = id => {
    if (selectionRef.current.id !== id) {
      selectionRef.current = {
        id,
        generation: selectionRef.current.generation + 1
      };
    }
  };
  advanceSelection(selectedPropertyId);
  // An upload the PM had not saved when this tab was last left comes back as it was, but only for the property, utility
  // and billing cycle it was parsed for; the screen around keeps it.
  const uploadScope = "".concat(propertyId, "|").concat(utilityId, "|").concat(billingCycleId);
  const keptUpload = (unsavedUpload === null || unsavedUpload === void 0 ? void 0 : unsavedUpload.scope) === uploadScope ? unsavedUpload : null;
  const _useState = useState(() => {
      var _keptUpload$parsedRow;
      return (_keptUpload$parsedRow = keptUpload === null || keptUpload === void 0 ? void 0 : keptUpload.parsedRows) !== null && _keptUpload$parsedRow !== void 0 ? _keptUpload$parsedRow : [];
    }),
    _useState2 = _slicedToArray(_useState, 2),
    parsedRows = _useState2[0],
    setParsedRows = _useState2[1];
  const _useState3 = useState(() => {
      var _keptUpload$parseCsvR;
      return (_keptUpload$parseCsvR = keptUpload === null || keptUpload === void 0 ? void 0 : keptUpload.parseCsvResponse) !== null && _keptUpload$parseCsvR !== void 0 ? _keptUpload$parseCsvR : null;
    }),
    _useState4 = _slicedToArray(_useState3, 2),
    parseCsvResponse = _useState4[0],
    setParseCsvResponse = _useState4[1];
  const _useState5 = useState(() => {
      var _keptUpload$parseWarn;
      return (_keptUpload$parseWarn = keptUpload === null || keptUpload === void 0 ? void 0 : keptUpload.parseWarnings) !== null && _keptUpload$parseWarn !== void 0 ? _keptUpload$parseWarn : [];
    }),
    _useState6 = _slicedToArray(_useState5, 2),
    parseWarnings = _useState6[0],
    setParseWarnings = _useState6[1];
  // Single-reading template. The openings are the starts not yet saved, keyed by unit plus normalized meter id: those a
  // Start reads file gave, those typed, matched or not. The start file's own state is the rows it held and the warnings
  // of its own; the end file's id and disclosure stay with parseCsvResponse. A file with a duplicate meter is refused.
  const _useState7 = useState(() => {
      var _keptUpload$openings;
      return new Map((_keptUpload$openings = keptUpload === null || keptUpload === void 0 ? void 0 : keptUpload.openings) !== null && _keptUpload$openings !== void 0 ? _keptUpload$openings : []);
    }),
    _useState8 = _slicedToArray(_useState7, 2),
    openings = _useState8[0],
    setOpenings = _useState8[1];
  const openingsRef = useRef(openings);
  const _useState9 = useState(() => {
      var _keptUpload$startFile;
      return (_keptUpload$startFile = keptUpload === null || keptUpload === void 0 ? void 0 : keptUpload.startFile) !== null && _keptUpload$startFile !== void 0 ? _keptUpload$startFile : null;
    }),
    _useState0 = _slicedToArray(_useState9, 2),
    startFile = _useState0[0],
    setStartFile = _useState0[1];
  const _useState1 = useState(null),
    _useState10 = _slicedToArray(_useState1, 2),
    refusedFile = _useState10[0],
    setRefusedFile = _useState10[1];
  // Set when the upload this screen saved is edited afterwards: the edit is not in the saved rows, so a return to this tab
  // shows the upload with the edit, not the saved rows (see loadSavedRows).
  const _useState11 = useState(() => {
      var _keptUpload$editedAft;
      return (_keptUpload$editedAft = keptUpload === null || keptUpload === void 0 ? void 0 : keptUpload.editedAfterSave) !== null && _keptUpload$editedAft !== void 0 ? _keptUpload$editedAft : false;
    }),
    _useState12 = _slicedToArray(_useState11, 2),
    editedAfterSave = _useState12[0],
    setEditedAfterSave = _useState12[1];
  const editedAfterSaveRef = useRef(editedAfterSave);
  editedAfterSaveRef.current = editedAfterSave;
  const commitOpenings = next => {
    openingsRef.current = next;
    setOpenings(next);
  };
  function resetPendingOpenings() {
    // Nothing to clear leaves the state alone, so a screen with no openings is not told it changed.
    if (openingsRef.current.size > 0) commitOpenings(new Map());
    setStartFile(null);
    setRefusedFile(null);
  }
  // The template the upload on screen was read under, as its id and the columns it maps. A template read later that is
  // not this one (another map, or the same map edited in place) means the upload no longer means what it did: it is
  // dropped with its openings. Kept with the upload across Bill details, and empty while the template was not known.
  const uploadTemplateKeyRef = useRef((_keptUpload$templateK = keptUpload === null || keptUpload === void 0 ? void 0 : keptUpload.templateKey) !== null && _keptUpload$templateK !== void 0 ? _keptUpload$templateK : null);
  const hasUploadRef = useRef(false);
  const displayedKindRef = useRef(null);
  function resetUpload() {
    uploadTemplateKeyRef.current = null;
    setEditedAfterSave(false);
    setParsedRows([]);
    setParseCsvResponse(null);
    setParseWarnings([]);
    resetPendingOpenings();
  }
  // A Save sends the starts as they are when it begins. A start uploaded or typed while it runs would be missing from what
  // it saved, so the starts cannot change until it has answered. An end file uploaded meanwhile is a new upload, and the
  // save only clears the one it sent.
  const _useState13 = useState(false),
    _useState14 = _slicedToArray(_useState13, 2),
    isUploadSaving = _useState14[0],
    setIsUploadSaving = _useState14[1];
  const isUploadSavingRef = useRef(false);
  const saveSeqRef = useRef(0);
  const gridEditSeqRef = useRef(0);
  // Text typed into a cell and not yet committed is newer than a reload of the saved rows that answers meanwhile; an
  // editor opened and left as the cell is, or typed back to what the cell holds, is not.
  const gridApiRef = useGridApiRef();
  const gridWrapperRef = useRef(null);
  // The grid takes an editor's text into its own state after a short delay, so the text is read where it is typed.
  const hasTypedEdit = () => {
    var _gridWrapperRef$curre, _gridWrapperRef$curre2;
    const api = gridApiRef.current;
    const editors = (_gridWrapperRef$curre = (_gridWrapperRef$curre2 = gridWrapperRef.current) === null || _gridWrapperRef$curre2 === void 0 ? void 0 : _gridWrapperRef$curre2.querySelectorAll('.MuiDataGrid-cell--editing')) !== null && _gridWrapperRef$curre !== void 0 ? _gridWrapperRef$curre : [];
    return [...editors].some(cell => {
      var _cell$closest, _api$getRow;
      const input = cell.querySelector('input');
      const rowId = (_cell$closest = cell.closest('[data-id]')) === null || _cell$closest === void 0 ? void 0 : _cell$closest.getAttribute('data-id');
      if (!input || rowId === null || rowId === undefined) return false;
      const held = api === null || api === void 0 || (_api$getRow = api.getRow(rowId)) === null || _api$getRow === void 0 ? void 0 : _api$getRow[cell.getAttribute('data-field')];
      const heldText = held === null || held === undefined ? '' : String(held);
      if (input.value === '' || heldText === '') return input.value !== heldText;
      return input.type === 'number' ? Number(input.value) !== Number(heldText) : input.value !== heldText;
    });
  };
  // A Save locks the grid, and the grid will not close an editor on a cell that is no longer editable. An editor still
  // open when Save begins (a click on the button normally commits it first) is closed, its uncommitted text dropped:
  // Save sends the rows as they are, and that text is not in them.
  const closeOpenEditors = () => {
    var _api$state$editRows, _api$state;
    const api = gridApiRef.current;
    Object.entries((_api$state$editRows = api === null || api === void 0 || (_api$state = api.state) === null || _api$state === void 0 ? void 0 : _api$state.editRows) !== null && _api$state$editRows !== void 0 ? _api$state$editRows : {}).forEach(_ref6 => {
      let _ref7 = _slicedToArray(_ref6, 2),
        id = _ref7[0],
        fields = _ref7[1];
      return Object.keys(fields).forEach(field => api.stopCellEditMode({
        id,
        field,
        ignoreModifications: true
      }));
    });
  };
  const savedParseRef = useRef(null);
  const _useState15 = useState(null),
    _useState16 = _slicedToArray(_useState15, 2),
    mappingOpenFor = _useState16[0],
    setMappingOpenFor = _useState16[1];
  useEffect(() => reaction(getSelectedPropertyId, id => {
    advanceSelection(id);
    setMappingOpenFor(null);
    resetPendingOpenings();
  }), [] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const _useState17 = useState(false),
    _useState18 = _slicedToArray(_useState17, 2),
    isOpeningMapping = _useState18[0],
    setIsOpeningMapping = _useState18[1];
  const isOpeningMappingRef = useRef(false);
  const uploadSeqRef = useRef(0);
  const columnMapReadsRef = useRef(getColumnMapVersion(propertyUtilityId));
  // A restored upload is visible on the first render, before effects or config reads run. Both the button and Save's
  // live guard must already be closed, and retries must re-read config until this upload's template is confirmed.
  const restoredMappingCheckRef = useRef(!!keptUpload);
  const needsMappingCheckRef = useRef(!!keptUpload);
  const mappingChecksRef = useRef(0);
  const mappingCheckSequenceRef = useRef(0);
  const latestMappingCheckRef = useRef(null);
  const failedMappingCheckRef = useRef(false);
  const _useState19 = useState(false),
    _useState20 = _slicedToArray(_useState19, 2),
    isMappingCheckFailed = _useState20[0],
    setIsMappingCheckFailed = _useState20[1];
  const setMappingCheckFailed = failed => {
    failedMappingCheckRef.current = failed;
    if (isActiveRef.current) setIsMappingCheckFailed(failed);
  };
  const _useState21 = useState(0),
    _useState22 = _slicedToArray(_useState21, 2),
    mappingCheckCount = _useState22[0],
    setMappingCheckCount = _useState22[1];
  const columnMapVersion = useSyncExternalStore(subscribeToColumnMapChanges, () => getColumnMapVersion(propertyUtilityId));
  const isMappingWriteInProgress = () => {
    var _store$propertyUtilit, _store$allocations$pu;
    return !!((_store$propertyUtilit = store.propertyUtility.updateCsvColumnMapAsync) !== null && _store$propertyUtilit !== void 0 && _store$propertyUtilit.inProgress || (_store$allocations$pu = store.allocations.putSubmeterBillingCsvMapAsync) !== null && _store$allocations$pu !== void 0 && _store$allocations$pu.inProgress);
  };
  const isMappingWriteRunning = isMappingWriteInProgress();
  const isMappingBusyNow = () => mappingChecksRef.current > 0 || failedMappingCheckRef.current || isMappingWriteInProgress() || needsMappingCheckRef.current || getColumnMapVersion(propertyUtilityId) !== columnMapReadsRef.current;
  const isMappingBusy = mappingCheckCount > 0 || isMappingCheckFailed || isMappingWriteRunning || needsMappingCheckRef.current || columnMapVersion !== columnMapReadsRef.current;
  const mappingOpenedDetailsRef = useRef(null);
  useEffect(() => {
    setMappingOpenFor(null);
  }, [selectedPropertyId]);
  const getSelectedPropertyDetails = () => {
    var _store$homeDashboardS2, _store$homeDashboardS3;
    return (_store$homeDashboardS2 = store.homeDashboardStore.getCurrentCompany) === null || _store$homeDashboardS2 === void 0 || (_store$homeDashboardS3 = _store$homeDashboardS2.selectedPropertyDetails) === null || _store$homeDashboardS3 === void 0 || (_store$homeDashboardS3 = _store$homeDashboardS3.call(_store$homeDashboardS2)) === null || _store$homeDashboardS3 === void 0 ? void 0 : _store$homeDashboardS3.details;
  };
  const currentPropertyDetails = getSelectedPropertyDetails();
  const canFixMapping = !((_store$account = store.account) !== null && _store$account !== void 0 && (_store$account = _store$account.featureFlagsStore) !== null && _store$account !== void 0 && _store$account.getIsBillingTemplatesEnabled) && selectedPropertyId === propertyId && !!currentPropertyDetails;
  const handleFixMapping = async () => {
    if (isOpeningMappingRef.current) return;
    isOpeningMappingRef.current = true;
    setIsOpeningMapping(true);
    const openedFor = getSelectedPropertyId();
    const details = currentPropertyDetails;
    const generation = selectionRef.current.generation;
    const isSameScreen = () => isActiveRef.current && selectionRef.current.generation === generation && getSelectedPropertyId() === openedFor && getSelectedPropertyDetails() === details;
    const showCouldNotOpen = () => toast('error', {
      body: ['Could not open the utility setup. Open it from the property Details tab.']
    });
    try {
      var _store$homeDashboardS4, _details$utilityTempl;
      // Only the Details tab loads the units the setup dialogs read; without them saving meter IDs deletes them all.
      const buildingId = (_store$homeDashboardS4 = store.homeDashboardStore.getCurrentProperty) === null || _store$homeDashboardS4 === void 0 || (_store$homeDashboardS4 = _store$homeDashboardS4.buildingInformation) === null || _store$homeDashboardS4 === void 0 ? void 0 : _store$homeDashboardS4.id;
      if (!buildingId) {
        showCouldNotOpen();
        return;
      }
      const units = await store.units.getUnitsAsync.run({
        buildingId,
        applyToHomeDashboard: true
      }, [propertyId]);
      if (!isSameScreen()) return;
      if (!(units !== null && units !== void 0 && units.isSuccessful)) {
        showCouldNotOpen();
        return;
      }
      const isLoaded = await details.fetchUtilityTemplates({
        forceUpdate: true
      });
      if (!isSameScreen()) return;
      if (isLoaded !== true) {
        showCouldNotOpen();
        return;
      }
      const template = (_details$utilityTempl = details.utilityTemplatesList) === null || _details$utilityTempl === void 0 ? void 0 : _details$utilityTempl.find(entry => entry.utilityId === utilityId);
      if (!template) {
        showCouldNotOpen();
        return;
      }
      details.setUtilityTemplateModalForm(template);
      mappingOpenedDetailsRef.current = details;
      setMappingOpenFor(openedFor);
    } catch (_unused) {
      if (isActiveRef.current) showCouldNotOpen();
    } finally {
      isOpeningMappingRef.current = false;
      if (isActiveRef.current) setIsOpeningMapping(false);
    }
  };
  const isMappingOpen = mappingOpenFor === selectedPropertyId && mappingOpenedDetailsRef.current === currentPropertyDetails;
  const _useState23 = useState(null),
    _useState24 = _slicedToArray(_useState23, 2),
    csvMapId = _useState24[0],
    setCsvMapId = _useState24[1];
  const _useState25 = useState(true),
    _useState26 = _slicedToArray(_useState25, 2),
    initialLoading = _useState26[0],
    setInitialLoading = _useState26[1];

  // What the saved template says about the files it reads: 'idle' while there is no template to read, 'loading' until
  // the first read answers, then 'ready' with whether it maps no start columns (a single reading template, which takes
  // a start reads file and an end reads file) and its key, or 'failed'. A template that is not known is neither kind:
  // a file is then read by what it shows itself to be, and a read is tried again with the next upload.
  const IDLE_TEMPLATE = {
    id: null,
    state: 'idle',
    isSingleReading: false,
    key: ''
  };
  const _useState27 = useState(IDLE_TEMPLATE),
    _useState28 = _slicedToArray(_useState27, 2),
    template = _useState28[0],
    setTemplate = _useState28[1];
  const templateRef = useRef(template);
  const templateReadRef = useRef(0);
  const latestTemplateReadRef = useRef(null);
  const csvMapIdRef = useRef(null);
  const applyTemplate = next => {
    const known = templateRef.current;
    if (known.id === next.id && known.state === next.state && known.key === next.key) return;
    if (next.state === 'ready') {
      const uploadKey = uploadTemplateKeyRef.current;
      if (uploadKey && uploadKey !== next.key) resetUpload();else if (!uploadKey && hasUploadRef.current) uploadTemplateKeyRef.current = next.key;
    }
    templateRef.current = next;
    setTemplate(next);
  };
  // One read in flight per map: a read for the map already being read is that read, unless it is forced (an upload, or
  // the setup closing, which have to see the mapping as it is now). A read that a newer one replaced answers with the
  // newest, so whoever waits on it gets the template in force when it lands.
  const readTemplate = function (mapId) {
    let _ref8 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref8$force = _ref8.force,
      force = _ref8$force === void 0 ? false : _ref8$force,
      _ref8$requireFresh = _ref8.requireFresh,
      requireFresh = _ref8$requireFresh === void 0 ? false : _ref8$requireFresh,
      _ref8$isCurrent = _ref8.isCurrent,
      isCurrent = _ref8$isCurrent === void 0 ? () => true : _ref8$isCurrent;
    const inFlight = latestTemplateReadRef.current;
    if (mapId && inFlight && inFlight.mapId === mapId && !force) return inFlight.promise;
    const read = ++templateReadRef.current;
    if (!mapId) {
      latestTemplateReadRef.current = null;
      applyTemplate(IDLE_TEMPLATE);
      return Promise.resolve(templateRef.current);
    }
    if (templateRef.current.id !== mapId) applyTemplate({
      id: mapId,
      state: 'loading',
      isSingleReading: false,
      key: ''
    });
    const promise = (async (_latestTemplateReadRe2, _latestTemplateReadRe3, _latestTemplateReadRe4) => {
      let next = null;
      try {
        const resp = await store.allocations.getSubmeterBillingCsvMapForUploadAsync.run({
          id: mapId
        });
        if (resp !== null && resp !== void 0 && resp.isSuccessful && resp.response) {
          next = {
            id: mapId,
            state: 'ready',
            isSingleReading: isSingleReadingTemplate(resp.response),
            key: toTemplateKey(_objectSpread(_objectSpread({}, resp.response), {}, {
              id: mapId
            }))
          };
        }
      } catch (_unused2) {
        // A template that cannot be read is not known; the next upload reads it again.
      }
      if (!isCurrent()) {
        var _latestTemplateReadRe;
        if (((_latestTemplateReadRe = latestTemplateReadRef.current) === null || _latestTemplateReadRe === void 0 ? void 0 : _latestTemplateReadRe.read) === read) latestTemplateReadRef.current = null;
        return null;
      }
      if (read !== templateReadRef.current) return (_latestTemplateReadRe2 = (_latestTemplateReadRe3 = latestTemplateReadRef.current) === null || _latestTemplateReadRe3 === void 0 ? void 0 : _latestTemplateReadRe3.promise) !== null && _latestTemplateReadRe2 !== void 0 ? _latestTemplateReadRe2 : templateRef.current;
      if (((_latestTemplateReadRe4 = latestTemplateReadRef.current) === null || _latestTemplateReadRe4 === void 0 ? void 0 : _latestTemplateReadRe4.read) === read) latestTemplateReadRef.current = null;
      if (!isActiveRef.current) return templateRef.current;
      // A failed read of the template already known keeps what is known.
      if (!next && templateRef.current.id === mapId && templateRef.current.state === 'ready') return requireFresh ? null : templateRef.current;
      applyTemplate(next !== null && next !== void 0 ? next : {
        id: mapId,
        state: 'failed',
        isSingleReading: false,
        key: ''
      });
      return templateRef.current;
    })();
    latestTemplateReadRef.current = {
      mapId,
      read,
      promise
    };
    return promise;
  };
  // The column map the utility uploads with, and what it maps, as they are now: the map is read again when the setup
  // noted a change, and the template always, since a map edited in place keeps its id. Resolves to the template, or to
  // null when the map could not be checked.
  const refreshMapping = function () {
    let _ref9 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref9$isSilent = _ref9.isSilent,
      isSilent = _ref9$isSilent === void 0 ? false : _ref9$isSilent;
    const check = ++mappingCheckSequenceRef.current;
    const isCurrentCheck = () => isActiveRef.current && check === mappingCheckSequenceRef.current;
    // A file already waiting on an older check must use the newest result, not lose its selected upload.
    const followCurrentCheck = () => {
      var _latestMappingCheckRe, _latestMappingCheckRe2, _latestMappingCheckRe3;
      return isActiveRef.current && ((_latestMappingCheckRe = latestMappingCheckRef.current) === null || _latestMappingCheckRe === void 0 ? void 0 : _latestMappingCheckRe.check) !== check ? (_latestMappingCheckRe2 = (_latestMappingCheckRe3 = latestMappingCheckRef.current) === null || _latestMappingCheckRe3 === void 0 ? void 0 : _latestMappingCheckRe3.promise) !== null && _latestMappingCheckRe2 !== void 0 ? _latestMappingCheckRe2 : null : null;
    };
    // A newer caller inherits an in-flight check's freshness requirement; it cannot supersede it with a cached fallback.
    const requireFresh = restoredMappingCheckRef.current || mappingChecksRef.current > 0 || needsMappingCheckRef.current || failedMappingCheckRef.current || getColumnMapVersion(propertyUtilityId) !== columnMapReadsRef.current;
    needsMappingCheckRef.current = false;
    setMappingCheckFailed(false);
    mappingChecksRef.current += 1;
    setMappingCheckCount(mappingChecksRef.current);
    const promise = (async () => {
      try {
        let mapId = csvMapIdRef.current;
        const changesSeen = getColumnMapVersion(propertyUtilityId);
        if (restoredMappingCheckRef.current || changesSeen !== columnMapReadsRef.current && propertyUtilityId) {
          var _config$response;
          if (!propertyUtilityId) {
            setMappingCheckFailed(true);
            return null;
          }
          const config = await store.propertyUtility.getSubmeterConfigV2Async.run({
            propertyUtilityId
          });
          if (!isCurrentCheck()) return followCurrentCheck();
          if (!(config !== null && config !== void 0 && config.isSuccessful)) {
            setMappingCheckFailed(true);
            if (!isSilent) toast('error', {
              body: ['Could not check the column mapping. Try the upload again.']
            });
            return null;
          }
          columnMapReadsRef.current = changesSeen;
          mapId = ((_config$response = config.response) === null || _config$response === void 0 ? void 0 : _config$response.csvColumnMapId) || null;
          csvMapIdRef.current = mapId;
          setCsvMapId(mapId);
        }
        const checked = await readTemplate(mapId, {
          force: true,
          requireFresh,
          isCurrent: isCurrentCheck
        });
        if (!isCurrentCheck()) return followCurrentCheck();
        const failed = requireFresh && (!checked || checked.state === 'failed' || checked.state === 'idle' && !!uploadTemplateKeyRef.current);
        setMappingCheckFailed(failed);
        if (!failed) restoredMappingCheckRef.current = false;
        return checked;
      } catch (_unused3) {
        if (!isCurrentCheck()) return followCurrentCheck();
        setMappingCheckFailed(true);
        return null;
      } finally {
        mappingChecksRef.current -= 1;
        if (isActiveRef.current) setMappingCheckCount(mappingChecksRef.current);
      }
    })();
    latestMappingCheckRef.current = {
      check,
      promise
    };
    return promise;
  };
  // Closing setup checks the current map id as well as its columns, so a map switch or an edit in place immediately
  // updates the upload areas and drops an upload read under the old template.
  const handleMappingClosed = () => {
    needsMappingCheckRef.current = true;
    setMappingOpenFor(null);
    if (!isMappingWriteInProgress()) refreshMapping();
  };
  // Set active or Map columns may settle after setup unmounts; both announce successful mapping changes.
  useEffect(() => {
    if (!isMappingOpen && !failedMappingCheckRef.current && !isMappingWriteRunning && mappingChecksRef.current === 0 && (needsMappingCheckRef.current || columnMapVersion !== columnMapReadsRef.current)) refreshMapping();
  }, [isMappingOpen, isMappingWriteRunning, columnMapVersion, mappingCheckCount]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    csvMapIdRef.current = csvMapId;
    if (csvMapId && templateRef.current.id === csvMapId && templateRef.current.state === 'ready') return;
    readTemplate(csvMapId);
  }, [csvMapId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Rows that carry where their start came from were read as a single reading file, whatever the template says now.
  const hasSingleRows = parsedRows.some(row => row.openingSource !== undefined);
  const isSingleReading = template.state === 'ready' ? template.isSingleReading : hasSingleRows;
  const areUploadAreasShown = template.state === 'ready' && template.isSingleReading;
  const isTemplatePending = template.state === 'loading' || isMappingBusy;
  displayedKindRef.current = parsedRows.length === 0 ? null : hasSingleRows ? 'single' : 'interval';
  hasUploadRef.current = parsedRows.length > 0 || !!parseCsvResponse || openings.size > 0 || !!startFile;
  useEffect(() => {
    // Restored uploads use refreshMapping above: config and template are one required, sequenced check.
    if (!propertyUtilityId || keptUpload) return;
    columnMapReadsRef.current = getColumnMapVersion(propertyUtilityId);
    store.propertyUtility.getSubmeterConfigV2Async.run({
      propertyUtilityId
    }).then(resp => {
      if (resp !== null && resp !== void 0 && resp.isSuccessful) {
        var _resp$response;
        csvMapIdRef.current = ((_resp$response = resp.response) === null || _resp$response === void 0 ? void 0 : _resp$response.csvColumnMapId) || null;
        setCsvMapId(csvMapIdRef.current);
      }
    });
  }, [propertyUtilityId]); // eslint-disable-line react-hooks/exhaustive-deps

  const reportUnsavedUpload = useRef(onUnsavedUploadChange);
  reportUnsavedUpload.current = onUnsavedUploadChange;
  const uploadScopeRef = useRef(uploadScope);
  uploadScopeRef.current = uploadScope;
  const isFirstUploadReportRef = useRef(true);
  useEffect(() => {
    var _reportUnsavedUpload$;
    // Skipped on mount: the state was just read from the kept upload (or is empty), so reporting it would only echo it back.
    if (isFirstUploadReportRef.current) {
      isFirstUploadReportRef.current = false;
      return;
    }
    // A parse that returned no rows is still a completed parse: its warnings are kept with it. Starts held before any end
    // file, and the start file's own state, are kept as well.
    (_reportUnsavedUpload$ = reportUnsavedUpload.current) === null || _reportUnsavedUpload$ === void 0 || _reportUnsavedUpload$.call(reportUnsavedUpload, parseCsvResponse || openings.size > 0 || startFile ? {
      scope: uploadScopeRef.current,
      parsedRows,
      parseCsvResponse,
      parseWarnings,
      openings: [...openings],
      startFile,
      templateKey: uploadTemplateKeyRef.current,
      editedAfterSave
    } : null);
  }, [parsedRows, parseCsvResponse, parseWarnings, openings, startFile, editedAfterSave]);
  // A new property, utility or cycle starts without the openings of the last one.
  const lastScopeRef = useRef(uploadScope);
  useEffect(() => {
    if (lastScopeRef.current === uploadScope) return;
    lastScopeRef.current = uploadScope;
    resetPendingOpenings();
  }, [uploadScope]); // eslint-disable-line react-hooks/exhaustive-deps
  const _useState29 = useState(null),
    _useState30 = _slicedToArray(_useState29, 2),
    savedAllocations = _useState30[0],
    setSavedAllocations = _useState30[1];
  const _useState31 = useState(() => new Set()),
    _useState32 = _slicedToArray(_useState31, 2),
    balancedMeters = _useState32[0],
    setBalancedMeters = _useState32[1];
  const applyLoadedRows = rows => {
    setSavedAllocations(rows);
    setBalancedMeters(toBalancedMeterKeys(rows));
  };
  const _useState33 = useState(null),
    _useState34 = _slicedToArray(_useState33, 2),
    variance = _useState34[0],
    setVariance = _useState34[1];
  const _useState35 = useState(false),
    _useState36 = _slicedToArray(_useState35, 2),
    isVarianceLoading = _useState36[0],
    setIsVarianceLoading = _useState36[1];
  const _useState37 = useState(false),
    _useState38 = _slicedToArray(_useState37, 2),
    isVarianceFailed = _useState38[0],
    setIsVarianceFailed = _useState38[1];
  const _useState39 = useState(false),
    _useState40 = _slicedToArray(_useState39, 2),
    isReviewing = _useState40[0],
    setIsReviewing = _useState40[1];
  const _useState41 = useState(null),
    _useState42 = _slicedToArray(_useState41, 2),
    unmappedUnits = _useState42[0],
    setUnmappedUnits = _useState42[1];
  const _useState43 = useState(false),
    _useState44 = _slicedToArray(_useState43, 2),
    isUnmappedConfirmOpen = _useState44[0],
    setIsUnmappedConfirmOpen = _useState44[1];
  const _useState45 = useState(false),
    _useState46 = _slicedToArray(_useState45, 2),
    isCheckingUnmapped = _useState46[0],
    setIsCheckingUnmapped = _useState46[1];
  const varianceReadIdRef = useRef(0);
  const unmappedReadIdRef = useRef(0);
  const unmappedReadRef = useRef(null);
  const unmappedAnswerRef = useRef(null);
  const liveCycleRef = useRef({});
  const utilityRef = useRef(utility);
  const unmappedContextRef = useRef(null);
  utilityRef.current = utility;
  const bindGenerationRef = useRef(0);
  const cycleKeyRef = useRef("".concat(propertyId, ":").concat(utilityId, ":").concat(billingCycleId));
  const cycleEpochRef = useRef(0);
  // Advances whenever the cycle's bill goes or comes back, so an approval answered across that change is not locked in.
  const billVersionRef = useRef(0);
  const _useState47 = useState(false),
    _useState48 = _slicedToArray(_useState47, 2),
    rowsLoadFailed = _useState48[0],
    setRowsLoadFailed = _useState48[1];
  const _useState49 = useState(false),
    _useState50 = _slicedToArray(_useState49, 2),
    isApproved = _useState50[0],
    setIsApproved = _useState50[1];
  // Locks the grid while the PUT and approve run, so no edit lands between them.
  const _useState51 = useState(false),
    _useState52 = _slicedToArray(_useState51, 2),
    isSaving = _useState52[0],
    setIsSaving = _useState52[1];
  const _useState53 = useState(false),
    _useState54 = _slicedToArray(_useState53, 2),
    isApproveFlowRunning = _useState54[0],
    setIsApproveFlowRunning = _useState54[1];
  const _useState55 = useState(false),
    _useState56 = _slicedToArray(_useState55, 2),
    isRecalculateConfirmOpen = _useState56[0],
    setIsRecalculateConfirmOpen = _useState56[1];
  const _useState57 = useState(false),
    _useState58 = _slicedToArray(_useState57, 2),
    isClearConfirmOpen = _useState58[0],
    setIsClearConfirmOpen = _useState58[1];
  const _useState59 = useState(false),
    _useState60 = _slicedToArray(_useState59, 2),
    isRecalculatedStale = _useState60[0],
    setIsRecalculatedStale = _useState60[1];
  const _useState61 = useState(false),
    _useState62 = _slicedToArray(_useState61, 2),
    isRefreshingRecalculatedRows = _useState62[0],
    setIsRefreshingRecalculatedRows = _useState62[1];
  const rebuildKey = "".concat(propertyId, ":").concat(utilityId, ":").concat(billingCycleId);
  const isRecalculating = useSyncExternalStore(subscribeToCycleWork, () => rebuildsInFlight.has(rebuildKey));
  const isRowWriteRunning = useSyncExternalStore(subscribeToCycleWork, () => rowWritesInFlight.has(rebuildKey));
  const isFirstReadDeferredRef = useRef(false);
  const _useState63 = useState(false),
    _useState64 = _slicedToArray(_useState63, 2),
    mustConfirmRows = _useState64[0],
    setMustConfirmRows = _useState64[1];
  const ranRebuildHereRef = useRef(false);
  const wasRecalculatingRef = useRef(false);
  const loadVariance = async () => {
    if (!isActiveRef.current) return null;
    const readId = ++varianceReadIdRef.current;
    setIsVarianceLoading(true);
    const resp = await store.allocations.getSubmeterBillingAllocationsVarianceAsync.run({
      propertyId,
      utilityId,
      billingCycleId
    });
    if (readId !== varianceReadIdRef.current) return null;
    setIsVarianceLoading(false);
    const landed = resp !== null && resp !== void 0 && resp.isSuccessful ? resp.response : null;
    if (typeof (landed === null || landed === void 0 ? void 0 : landed.requiresAcknowledgement) !== 'boolean') {
      setVariance(null);
      setIsVarianceFailed(true);
      return null;
    }
    setVariance(landed);
    setIsVarianceFailed(false);
    return landed;
  };
  const loadUnmappedUnits = () => {
    if (!isActiveRef.current || cycleKeyRef.current !== "".concat(propertyId, ":").concat(utilityId, ":").concat(billingCycleId)) {
      return Promise.resolve();
    }
    const readId = ++unmappedReadIdRef.current;
    const read = store.allocations.getSubmeterBillingAllocationsUnmappedBillableUnitsAsync.run({
      propertyId,
      utilityId,
      billingCycleId,
      includeMapped: true
    }).then(resp => {
      if (readId !== unmappedReadIdRef.current) return;
      const answer = resp !== null && resp !== void 0 && resp.isSuccessful && Array.isArray(resp.response) ? resp.response : null;
      unmappedAnswerRef.current = answer;
      setUnmappedUnits(answer);
    });
    unmappedReadRef.current = read;
    return read;
  };
  const settledUnmappedUnits = async () => {
    let read;
    do {
      read = unmappedReadRef.current;
      await read;
    } while (read !== unmappedReadRef.current);
    return unmappedAnswerRef.current;
  };
  const loadSavedRows = () => {
    const epoch = cycleEpochRef.current;
    setRowsLoadFailed(false);
    setInitialLoading(true);
    return store.allocations.getSubmeterBillingAllocationsAsync.run({
      propertyId,
      utilityId,
      billingCycleId
    }).then(resp => {
      if (epoch !== cycleEpochRef.current) return;
      if (!(resp !== null && resp !== void 0 && resp.isSuccessful) || !Array.isArray(resp.response)) {
        setRowsLoadFailed(true);
        loadUnmappedUnits();
        return;
      }
      setMustConfirmRows(false);
      if (resp.response.length > 0 && editedAfterSaveRef.current) {
        // The server holds this upload as it was saved, and the PM has edited it since: the edit is what this tab
        // shows, as an upload not saved yet, and never as saved rows.
        loadUnmappedUnits();
      } else if (resp.response.length > 0) {
        var _reportUnsavedUpload$2;
        applyLoadedRows(resp.response);
        // Rows the server holds are what this tab shows; an upload from before is dropped, and its warnings with it.
        setParsedRows([]);
        setParseCsvResponse(null);
        setParseWarnings([]);
        resetPendingOpenings();
        if (isActiveRef.current) (_reportUnsavedUpload$2 = reportUnsavedUpload.current) === null || _reportUnsavedUpload$2 === void 0 || _reportUnsavedUpload$2.call(reportUnsavedUpload, null);
        loadVariance();
        loadUnmappedUnits();
      } else {
        onNoRows === null || onNoRows === void 0 || onNoRows();
        loadUnmappedUnits();
      }
    }).finally(() => {
      if (epoch === cycleEpochRef.current) setInitialLoading(false);
    });
  };
  useEffect(() => {
    if (!propertyId || !utilityId || !billingCycleId) {
      setInitialLoading(false);
      return;
    }
    cycleEpochRef.current += 1;
    cycleKeyRef.current = "".concat(propertyId, ":").concat(utilityId, ":").concat(billingCycleId);
    varianceReadIdRef.current += 1;
    unmappedReadIdRef.current += 1;
    unmappedReadRef.current = null;
    unmappedAnswerRef.current = null;
    setVariance(null);
    setIsVarianceLoading(false);
    setIsVarianceFailed(false);
    setUnmappedUnits(null);
    setIsUnmappedConfirmOpen(false);
    setIsApproved(false);
    setIsRefreshingRecalculatedRows(false);
    wasRecalculatingRef.current = false;
    ranRebuildHereRef.current = false;
    // Rows read while another screen's row write runs could predate it, and a Save from them would undo it.
    if (rowWritesInFlight.has(rebuildKey)) {
      isFirstReadDeferredRef.current = true;
      setMustConfirmRows(true);
      setInitialLoading(true);
      return;
    }
    isFirstReadDeferredRef.current = false;
    setMustConfirmRows(false);
    loadSavedRows();
  }, [propertyId, utilityId, billingCycleId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isRowWriteRunning || !isFirstReadDeferredRef.current) return;
    isFirstReadDeferredRef.current = false;
    loadSavedRows();
  }, [isRowWriteRunning]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentCompany = store.homeDashboardStore.getCurrentCompany || {};
  const applyBill = currentCompany.billingTab.applyBill || {};
  const allocationCycleId = applyBill.allocationCycleId;
  // After the statement is sent out the meter details are read-only, no re-save/approve.
  const isStatementSent = applyBill.status === 'TableReadyStatementSent';
  // The server's table read leaves the utility's live bill empty once the cycle has none. Nothing is offered then.
  const isBillRemoved = !!utility && !utility.bill;
  const isCycleApproved = isApproved || applyBill.status === 'TableReady' || isStatementSent;
  useEffect(() => {
    billVersionRef.current += 1;
    if (isBillRemoved) {
      setIsApproved(false);
      setIsRecalculateConfirmOpen(false);
      setIsClearConfirmOpen(false);
      closeOldServicePeriodRef.current();
    }
  }, [isBillRemoved]);
  const billKey = "".concat(utility === null || utility === void 0 || (_utility$bill = utility.bill) === null || _utility$bill === void 0 ? void 0 : _utility$bill.id, ":").concat(utility === null || utility === void 0 || (_utility$bill2 = utility.bill) === null || _utility$bill2 === void 0 ? void 0 : _utility$bill2.startDateOnly, ":").concat(utility === null || utility === void 0 || (_utility$bill3 = utility.bill) === null || _utility$bill3 === void 0 ? void 0 : _utility$bill3.endDateOnly);
  const billKeyRef = useRef(billKey);
  useEffect(() => {
    if (billKeyRef.current === billKey) return;
    billKeyRef.current = billKey;
    bindGenerationRef.current += 1;
    if (!isBillRemoved) loadUnmappedUnits();
  }, [billKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const billUsageUnits = toUsageUnits(billUsage);
  // Each grid's total is worked out once and handed to both the metered usage warning and the unit notes.
  // The saved total counts a meter shared by several residencies once; its key is the unit id plus the serial.
  const savedMeteredUnits = savedAllocations ? sumMeteredUsage(savedAllocations, true) : 0;
  const parsedMeteredUnits = sumMeteredUsage(parsedRows, false);
  const renderUsageWarning = meteredUnits => !isStatementSent && billUsageUnits > 0 && meteredUnits > billUsageUnits ? /*#__PURE__*/React.createElement("div", {
    className: classes.usageWarning
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.usageWarningContent,
    role: "status"
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "warning",
    state: "warning"
  }), /*#__PURE__*/React.createElement("span", null, "Metered usage (".concat(formatUsageUnits(meteredUnits), ") is more than the bill usage (").concat(formatUsageUnits(billUsageUnits), "). Check Bill usage on the Bill details tab or the uploaded reads.")))) : null;

  // The reads' unit beside the bill usage, and a warning when the two look like different units. Only the
  // reads' unit is named; nothing is converted. Reads with no unit, or with more than one, cannot be checked
  // against the bill usage, so a general warning asks for the units to be checked instead.
  const renderUsageUnitNotes = (unitRows, meteredUnits) => {
    if (billUsageUnits <= 0 || meteredUnits <= 0) return null;
    const readsUnits = findReadsUnits(unitRows);
    const readsUnit = readsUnits.length === 1 ? readsUnits[0] : '';
    const renderUnitWarning = (key, message) => /*#__PURE__*/React.createElement("div", {
      key: key,
      className: classes.usageWarning
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.usageWarningContent,
      role: "status"
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "warning",
      state: "warning"
    }), /*#__PURE__*/React.createElement("span", null, message)));
    let unitWarning = null;
    if (!isStatementSent) {
      if (readsUnits.length === 0) {
        unitWarning = renderUnitWarning('no-unit', 'The reads have no unit, so the bill usage could not be checked against them. Check the units on Bill details and the file.');
      } else if (readsUnits.length > 1) {
        unitWarning = renderUnitWarning('mixed-units', "The reads use more than one unit (".concat(readsUnits.join(', '), "), so the bill usage could not be checked against them. Check the units on Bill details and the file."));
      } else if (isBillUsageUnitMismatch(billUsageUnits, meteredUnits)) {
        unitWarning = renderUnitWarning('unit-mismatch', "The bill usage looks like it is in a different unit than the reads (".concat(readsUnit, "). Enter the bill usage in ").concat(readsUnit, " on Bill details."));
      }
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: classes.usageNote
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2"
    }, "Bill usage: ".concat(formatUsageUnits(billUsageUnits), " (bill) vs ").concat(formatUsageUnits(meteredUnits)).concat(readsUnit ? " ".concat(readsUnit) : '', " (reads)"))), unitWarning);
  };
  const renderMeterTotalWarnings = meterRows => {
    var _utility$bill4, _utility$bill5;
    return isStatementSent || isBillRemoved ? null : toMeterTotalMismatches(meterRows, balancedMeters, toPeriodDays(utility === null || utility === void 0 || (_utility$bill4 = utility.bill) === null || _utility$bill4 === void 0 ? void 0 : _utility$bill4.startDateOnly, utility === null || utility === void 0 || (_utility$bill5 = utility.bill) === null || _utility$bill5 === void 0 ? void 0 : _utility$bill5.endDateOnly)).map(_ref0 => {
      let key = _ref0.key,
        message = _ref0.message;
      return /*#__PURE__*/React.createElement("div", {
        key: key,
        className: classes.usageWarning
      }, /*#__PURE__*/React.createElement("div", {
        className: classes.usageWarningContent,
        role: "status"
      }, /*#__PURE__*/React.createElement(Icon, {
        type: "warning",
        state: "warning"
      }), /*#__PURE__*/React.createElement("span", null, message)));
    });
  };

  // Single-reading template: where the starts taken from the earlier cycle are from, and what the grid says about them.
  const notLastMonthWarning = findNotLastMonthWarning(parseCsvResponse);
  const baselineMonth = toBaselineMonth({
    // The billing page keeps its summaries with the company, or with the selected property when it shows only that one.
    billingSummary: [...((_currentCompany$billi = (_currentCompany$billi2 = currentCompany.billingTab) === null || _currentCompany$billi2 === void 0 ? void 0 : _currentCompany$billi2.billingSummary) !== null && _currentCompany$billi !== void 0 ? _currentCompany$billi : []), ...((_store$homeDashboardS5 = (_store$homeDashboardS6 = (_store$homeDashboardS7 = store.homeDashboardStore).getPropertyById) === null || _store$homeDashboardS6 === void 0 || (_store$homeDashboardS6 = _store$homeDashboardS6.call(_store$homeDashboardS7, propertyId)) === null || _store$homeDashboardS6 === void 0 || (_store$homeDashboardS6 = _store$homeDashboardS6.allocations) === null || _store$homeDashboardS6 === void 0 ? void 0 : _store$homeDashboardS6.billingSummary) !== null && _store$homeDashboardS5 !== void 0 ? _store$homeDashboardS5 : [])],
    propertyId,
    baselineBillingCycleId: (_parseCsvResponse$bas = parseCsvResponse === null || parseCsvResponse === void 0 ? void 0 : parseCsvResponse.baselineBillingCycleId) !== null && _parseCsvResponse$bas !== void 0 ? _parseCsvResponse$bas : null,
    billingCycleId,
    notLastMonth: notLastMonthWarning
  });
  const pendingOpenings = isSingleReading ? findPendingOpenings(parsedRows, openings) : [];
  const shownWarnings = isSingleReading ? [...parseWarnings, ...((_startFile$warnings = startFile === null || startFile === void 0 ? void 0 : startFile.warnings) !== null && _startFile$warnings !== void 0 ? _startFile$warnings : []), ...toOpeningWarnings(parsedRows, {
    notLastMonthWarning
  })] : parseWarnings;
  // The end file says whether any earlier cycle could give the starts: with none, every row needs one from a start reads
  // file or from the grid. Until an end file is read it cannot be told.
  const startMark = parseCsvResponse ? parseCsvResponse.baselineBillingCycleId ? 'Optional' : 'Required' : null;
  let startHelp = 'Start reads can be uploaded before or after the end reads.';
  if (startMark === 'Required') {
    startHelp = 'No earlier cycle has start reads to use. Upload them here, or type them in the Start read column.';
  } else if (startMark === 'Optional') {
    startHelp = 'Start reads come from the earlier cycle. Upload a file to replace them for the meters it lists.';
  }
  const endRowCount = (_parseCsvResponse$row = parseCsvResponse === null || parseCsvResponse === void 0 || (_parseCsvResponse$row2 = parseCsvResponse.rows) === null || _parseCsvResponse$row2 === void 0 ? void 0 : _parseCsvResponse$row2.length) !== null && _parseCsvResponse$row !== void 0 ? _parseCsvResponse$row : 0;
  const isParseBusy = store.allocations.postSubmeterBillingParseCsvAsync.inProgress || isRecalculating;
  const rows = parsedRows.map((row, idx) => {
    var _row$unitName, _row$residentFullName, _row$meterSerialNumbe2, _row$startMeterRead, _row$endMeterRead, _row$startMeterDateOn, _row$endMeterDateOnly, _row$meterUsage, _row$meterRate, _row$unitsOfMeasure, _row$allocationCharge, _row$serviceDaysInPer, _row$isRateOverridden;
    return {
      id: idx,
      unitName: (_row$unitName = row.unitName) !== null && _row$unitName !== void 0 ? _row$unitName : '',
      residentFullName: (_row$residentFullName = row.residentFullName) !== null && _row$residentFullName !== void 0 ? _row$residentFullName : '',
      meterSerialNumber: (_row$meterSerialNumbe2 = row.meterSerialNumber) !== null && _row$meterSerialNumbe2 !== void 0 ? _row$meterSerialNumbe2 : '',
      startMeterRead: (_row$startMeterRead = row.startMeterRead) !== null && _row$startMeterRead !== void 0 ? _row$startMeterRead : '',
      endMeterRead: (_row$endMeterRead = row.endMeterRead) !== null && _row$endMeterRead !== void 0 ? _row$endMeterRead : '',
      startMeterDateOnly: (_row$startMeterDateOn = row.startMeterDateOnly) !== null && _row$startMeterDateOn !== void 0 ? _row$startMeterDateOn : '',
      endMeterDateOnly: (_row$endMeterDateOnly = row.endMeterDateOnly) !== null && _row$endMeterDateOnly !== void 0 ? _row$endMeterDateOnly : '',
      meterUsage: (_row$meterUsage = row.meterUsage) !== null && _row$meterUsage !== void 0 ? _row$meterUsage : '',
      meterRate: (_row$meterRate = row.meterRate) !== null && _row$meterRate !== void 0 ? _row$meterRate : '',
      unitsOfMeasure: (_row$unitsOfMeasure = row.unitsOfMeasure) !== null && _row$unitsOfMeasure !== void 0 ? _row$unitsOfMeasure : '',
      allocationCharge: (_row$allocationCharge = row.allocationCharge) !== null && _row$allocationCharge !== void 0 ? _row$allocationCharge : '',
      serviceDaysInPeriod: (_row$serviceDaysInPer = row.serviceDaysInPeriod) !== null && _row$serviceDaysInPer !== void 0 ? _row$serviceDaysInPer : '',
      isRateOverridden: (_row$isRateOverridden = row.isRateOverridden) !== null && _row$isRateOverridden !== void 0 ? _row$isRateOverridden : false,
      startSource: toSourceLabel(row.openingSource, baselineMonth)
    };
  });
  const toUnitNames = units => units.map(name => name ? t('allocations.meter_details_missing_reads_unit', {
    name
  }) : t('allocations.meter_details_missing_reads_unnamed_unit')).join(', ');
  const blockIfReadsMissing = rowsToSend => {
    const units = findUnitsMissingReads(rowsToSend);
    if (!units.length) return false;
    toast('error', {
      body: [t('allocations.meter_details_missing_reads', {
        units: toUnitNames(units)
      })]
    });
    return true;
  };

  // A start in one unit of measure and an end in another, or a start date after the end date, is not a reading to
  // charge from. An interval template only warns about the date, as it always did.
  const blockIfSingleReadingConflicts = rowsToSend => {
    const unitsOf = conflictRows => [...new Set(conflictRows.map(row => {
      var _row$unitName2;
      return (_row$unitName2 = row.unitName) !== null && _row$unitName2 !== void 0 ? _row$unitName2 : '';
    }))];
    const mismatched = unitsOf(findUnitConflictRows(rowsToSend));
    if (mismatched.length) {
      toast('error', {
        body: [t('allocations.meter_details_uom_conflict', {
          units: toUnitNames(mismatched)
        })]
      });
      return true;
    }
    const late = unitsOf(findStartAfterEndRows(rowsToSend));
    if (late.length) {
      toast('error', {
        body: [t('allocations.meter_details_start_after_end', {
          units: toUnitNames(late)
        })]
      });
      return true;
    }
    return false;
  };

  // Saved rows only: a row stored with a Usage that is not end minus start (it cannot be edited, only repaired by
  // editing a read or Recalculate) would be sent as it is, and a rate edit would price it on that Usage.
  const blockIfUsageMismatch = rowsToSend => {
    const units = findUnitsWithUsageMismatch(rowsToSend);
    if (!units.length) return false;
    const names = units.map(name => name ? t('allocations.meter_details_missing_reads_unit', {
      name
    }) : t('allocations.meter_details_missing_reads_unnamed_unit'));
    toast('error', {
      body: [t('allocations.meter_details_usage_mismatch', {
        units: names.join(', ')
      })]
    });
    return true;
  };

  // End below Start gives a negative usage and negative charges, which the backend refuses. Upload and saved grid.
  const blockIfEndBelowStart = rowsToSend => {
    const units = findUnitsWithEndBelowStart(rowsToSend);
    if (!units.length) return false;
    const names = units.map(name => name ? t('allocations.meter_details_missing_reads_unit', {
      name
    }) : t('allocations.meter_details_missing_reads_unnamed_unit'));
    toast('error', {
      body: [t('allocations.meter_details_end_below_start', {
        units: names.join(', ')
      })]
    });
    return true;
  };
  const blockIfNegativeReads = rowsToSend => {
    const units = findUnitsWithNegativeReads(rowsToSend);
    if (!units.length) return false;
    toast('error', {
      body: [t('allocations.meter_details_negative_read', {
        units: toUnitNames(units)
      })]
    });
    return true;
  };
  const blockIfReadsAboveMax = rowsToSend => {
    const units = findUnitsWithReadsAboveMax(rowsToSend);
    if (!units.length) return false;
    toast('error', {
      body: [t('allocations.meter_details_read_above_max', {
        units: toUnitNames(units)
      })]
    });
    return true;
  };
  const renderInvalidReadsWarning = rowsToCheck => {
    const messages = [[findUnitsWithEndBelowStart(rowsToCheck), 'allocations.meter_details_end_below_start'], [findUnitsWithNegativeReads(rowsToCheck), 'allocations.meter_details_negative_read'], [findUnitsWithReadsAboveMax(rowsToCheck), 'allocations.meter_details_read_above_max']].filter(_ref1 => {
      let _ref10 = _slicedToArray(_ref1, 1),
        units = _ref10[0];
      return units.length;
    }).map(_ref11 => {
      let _ref12 = _slicedToArray(_ref11, 2),
        units = _ref12[0],
        key = _ref12[1];
      return t(key, {
        units: toUnitNames(units)
      });
    });
    if (!messages.length) return null;
    return /*#__PURE__*/React.createElement("div", {
      className: classes.usageWarning
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.usageWarningContent,
      role: "alert"
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "warning",
      state: "error"
    }), /*#__PURE__*/React.createElement("span", null, messages.join(' '))));
  };
  const processRowUpdate = (newRow, oldRow) => {
    var _newRow$startMeterDat, _oldRow$startMeterDat;
    // The grid calls this on every committed cell exit, even when nothing was typed, so whether the
    // rate was overridden is taken from the grid's own before/after pair. Comparing the cell against
    // the parsed CSV instead would report edits the manager never made, and a false override is not
    // cosmetic: the server prices the override on the row it was typed on rather than grouping by
    // unit.
    const _resolveRateEdit = resolveRateEdit(oldRow.meterRate, newRow.meterRate, oldRow.isRateOverridden),
      isValid = _resolveRateEdit.isValid,
      isRateOverridden = _resolveRateEdit.isRateOverridden;
    if (!isValid) {
      toast('error', {
        body: ['A rate override has to be a number of zero or more and below 1,000,000,000.']
      });
      return oldRow;
    }
    track(events.changed('Parsed Meter Row'), {
      page,
      unit: newRow.unitName
    });

    // The grid renders from the row this returns, not from parsedRows, so the flag has to travel on
    // the returned row or the marker never reaches the cell.
    const storedReads = {
      startMeterRead: toStoredRead(newRow.startMeterRead),
      endMeterRead: toStoredRead(newRow.endMeterRead)
    };
    const readsChanged = haveReadsChanged(storedReads, oldRow);
    // A start typed into the grid is an opening of its own, kept like one a file gave, so an end file uploaded later
    // does not lose it. A start cleared is an opening too, an empty one the earlier cycle does not fill again.
    const isStartEdited = isSingleReading && (!isSameRead(storedReads.startMeterRead, oldRow.startMeterRead) || ((_newRow$startMeterDat = newRow.startMeterDateOnly) !== null && _newRow$startMeterDat !== void 0 ? _newRow$startMeterDat : '') !== ((_oldRow$startMeterDat = oldRow.startMeterDateOnly) !== null && _oldRow$startMeterDat !== void 0 ? _oldRow$startMeterDat : ''));
    let startFields = {};
    if (isStartEdited) {
      const current = _objectSpread(_objectSpread(_objectSpread({}, parsedRows[newRow.id]), newRow), storedReads);
      const key = toOpeningKey(current);
      const opening = toTypedOpening(current, openingsRef.current.get(key));
      commitOpenings(new Map(openingsRef.current).set(key, opening));
      const source = isReadEmpty(opening.startMeterRead) ? null : opening.source;
      startFields = {
        openingSource: source,
        openingUnitsOfMeasure: opening.unitsOfMeasure,
        startSource: toSourceLabel(source, baselineMonth)
      };
    }
    const updatedRow = _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, newRow), storedReads), {}, {
      isRateOverridden
    }, readsChanged && {
      meterUsage: toUsageFromReads(storedReads.startMeterRead, storedReads.endMeterRead)
    }), startFields);

    // An edit that changed a cell supersedes a reload of the saved rows still on its way; a cell left as it was does not.
    if (Object.keys(newRow).some(key => newRow[key] !== oldRow[key])) {
      gridEditSeqRef.current += 1;
      if (savedParseRef.current && savedParseRef.current === parseCsvResponse) setEditedAfterSave(true);
    }
    setParsedRows(prev => prev.map((row, idx) => idx === newRow.id ? _objectSpread(_objectSpread({}, row), updatedRow) : row));
    return updatedRow;
  };
  const notifyParseOutcome = (rowCount, warnings) => {
    if (rowCount === 0 && warnings.some(w => w.code === 'missing_required_column')) {
      toast('error', {
        body: ['This file is missing a required column. See the messages above the table.']
      });
      return;
    }
    if (rowCount === 0 && warnings.length === 0) {
      toast('error', {
        body: ['This file has no data rows.']
      });
      return;
    }
    if (rowCount === 0) {
      const unmatchedOnly = warnings.every(w => UNMATCHED_PARSE_CODES.includes(w.code));
      toast('error', {
        body: [unmatchedOnly ? 'No rows matched the units on this utility. See the messages above the table.' : 'No rows could be read from this file. See the messages above the table.']
      });
      return;
    }
    if (warnings.length === 0) {
      toast('success', {
        body: ['CSV parsed successfully!']
      });
      return;
    }
    const skipped = warnings.filter(w => SKIPPED_PARSE_CODES.includes(w.code)).length;
    const parsed = "".concat(rowCount, " ").concat(rowCount === 1 ? 'row' : 'rows');
    toast('warning', {
      body: ["Parsed ".concat(parsed, ", ").concat(skipped, " skipped. See the messages above the table.")]
    });
  };
  const refuseFile = (label, warnings) => {
    setRefusedFile({
      label,
      warnings
    });
    toast('error', {
      body: [toRefusedFileMessage(label)]
    });
  };

  // The end reads set the rows. What the file gave as a start is from the earlier cycle, and the openings held, uploaded
  // or typed, are laid over the rows they match. The file's own warnings are kept; the ones about starts are worked out
  // from the grid.
  const applyEndFile = response => {
    const ownWarnings = withoutOpeningWarnings(response.warnings);
    if (hasDuplicateMeters(ownWarnings)) {
      refuseFile('End reads', ownWarnings);
      return;
    }
    const endRows = response.rows.map(row => toEndRow(row, {
      toInitialRate: toInitialMeterRate
    }));
    setParsedRows(applyOpenings(endRows, openingsRef.current));
    setParseCsvResponse(response);
    setEditedAfterSave(false);
    setParseWarnings(ownWarnings);
    setRefusedFile(null);
    track(events.fileUploaded('Meter Reads CSV'), {
      page,
      rowsCount: response.rows.length
    });
    notifyParseOutcome(response.rows.length, ownWarnings);
  };

  // The start reads give the opening of each meter: a row's own end read and end date. Its inferred start, usage and
  // baseline cycle are not used, and nothing but the rows it matches changes. The rest are held for an end file.
  const applyStartFile = response => {
    const ownWarnings = withoutOpeningWarnings(response.warnings);
    if (hasDuplicateMeters(ownWarnings)) {
      refuseFile('Start reads', ownWarnings);
      return;
    }
    const incoming = new Map(response.rows.map(row => [toOpeningKey(row), toUploadedOpening(row)]));
    commitOpenings(new Map([...openingsRef.current, ...incoming]));
    setParsedRows(prev => applyOpenings(prev, incoming));
    setStartFile({
      rowCount: response.rows.length,
      warnings: ownWarnings
    });
    setRefusedFile(null);
    track(events.fileUploaded('Start Reads CSV'), {
      page,
      rowsCount: response.rows.length
    });
    notifyParseOutcome(response.rows.length, ownWarnings);
  };
  const handleCsvUpload = async function (e) {
    var _input$files;
    let kind = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'end';
    const input = e.target;
    const file = (_input$files = input.files) === null || _input$files === void 0 ? void 0 : _input$files[0];
    if (!file) return;
    if (kind === 'start' && isUploadSavingRef.current) {
      input.value = '';
      return;
    }
    const uploadedFor = getSelectedPropertyId();
    const generation = selectionRef.current.generation;
    const scopeAtStart = uploadScopeRef.current;
    uploadSeqRef.current += 1;
    const uploadSeq = uploadSeqRef.current;
    const isStillHere = () => isActiveRef.current && uploadSeqRef.current === uploadSeq && uploadScopeRef.current === scopeAtStart && selectionRef.current.generation === generation && getSelectedPropertyId() === uploadedFor;
    try {
      var _resp$response2, _resp$response$rows, _resp$response$warnin;
      const csvContent = await file.text();
      if (!isStillHere()) return;
      // The mapping is read as it is now, and the file waits for it: a file is never read under a template that is
      // still being read, or that an edit has since replaced.
      const requestTemplate = await refreshMapping();
      if (!isStillHere() || !requestTemplate) return;
      const requestKey = requestTemplate.state === 'ready' ? requestTemplate.key : '';
      const resp = await store.allocations.postSubmeterBillingParseCsvAsync.run({
        propertyId,
        utilityId,
        csvMapId: csvMapIdRef.current,
        billingCycleId,
        csvContent,
        isFirstCycleSecondFile: false
      });
      if (!isStillHere()) return;
      if (!(resp !== null && resp !== void 0 && resp.isSuccessful) || !Array.isArray((_resp$response2 = resp.response) === null || _resp$response2 === void 0 ? void 0 : _resp$response2.rows)) return;
      // What the file is read as is what the template in force now says. A template that is not known leaves it to the
      // file; one that changed while the file was read drops the answer, which was parsed for another mapping.
      const landedTemplate = templateRef.current;
      const landedKey = landedTemplate.state === 'ready' ? landedTemplate.key : '';
      if (requestKey && requestKey !== landedKey) {
        toast('error', {
          body: ['The column mapping changed while the file was read. Upload the file again.']
        });
        return;
      }
      // A start answer that lands while Save runs is not applied: Save sent the starts as they were, and the kept upload
      // is cleared by what it sent, so a newer opening would be shown but never saved.
      if (kind === 'start' && isUploadSavingRef.current) {
        toast('error', {
          body: ['A save was running, so the start reads were not applied. Upload the file again.']
        });
        return;
      }
      // While the template is not known, a file shows what it is by its rows. A file with no rows shows nothing, so it
      // keeps the kind of the upload on screen: the rows on screen decide, and the openings held decide only when no
      // rows are on screen. A duplicate file with no valid rows is then refused, not read as interval.
      const displayedKind = displayedKindRef.current;
      const keepsSingleKind = ((_resp$response$rows = resp.response.rows) !== null && _resp$response$rows !== void 0 ? _resp$response$rows : []).length === 0 && (displayedKind ? displayedKind === 'single' : openingsRef.current.size > 0);
      const isSingle = landedTemplate.state === 'ready' ? landedTemplate.isSingleReading : looksSingleReading(resp.response) || keepsSingleKind;
      uploadTemplateKeyRef.current = landedKey || null;
      if (isSingle) {
        if (kind === 'start') applyStartFile(resp.response);else applyEndFile(resp.response);
        return;
      }
      // Only a single reading template has a Start reads file.
      if (kind === 'start') return;
      // Parse always returns meterRate: 0, a placeholder the server replaces at save. Normalising it
      // to blank here, once, is what lets a manager type 0 later and have it register as an edit.
      // Reads are held at the four decimals the backend stores from here on, so the grid, the bill usage
      // warning and Save all show and send the one Usage.
      const normalizedRows = resp.response.rows.map(row => {
        const startMeterRead = toStoredRead(row.startMeterRead);
        const endMeterRead = toStoredRead(row.endMeterRead);
        return _objectSpread(_objectSpread({}, row), {}, {
          startMeterRead,
          endMeterRead,
          meterRate: toInitialMeterRate(row.meterRate),
          meterUsage: toUsageFromReads(startMeterRead, endMeterRead)
        });
      });
      const warnings = (_resp$response$warnin = resp.response.warnings) !== null && _resp$response$warnin !== void 0 ? _resp$response$warnin : [];
      setParsedRows(normalizedRows);
      setParseCsvResponse(resp.response);
      setEditedAfterSave(false);
      setParseWarnings(warnings);
      // Openings held for the single reading upload before this one stay held: an interval grid never applies them, and
      // a single reading upload that follows still finds them.
      track(events.fileUploaded('Meter Reads CSV'), {
        page,
        rowsCount: resp.response.rows.length
      });
      notifyParseOutcome(resp.response.rows.length, warnings);
    } catch (_unused4) {
      toast('error', {
        body: ['Failed to parse CSV']
      });
    } finally {
      input.value = '';
    }
  };

  // Reload after a write so the grid shows the backend's rounding, not the typed values.
  const reloadRows = async function () {
    let isSuperseded = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : () => false;
    if (!isActiveRef.current) return;
    const allocResp = await store.allocations.getSubmeterBillingAllocationsAsync.run({
      propertyId,
      utilityId,
      billingCycleId
    });
    // A reload given up on (see handleSave) that something newer has since overtaken says nothing: what is on screen is newer.
    if (isSuperseded()) return;
    if (allocResp !== null && allocResp !== void 0 && allocResp.isSuccessful && Array.isArray(allocResp.response)) {
      applyLoadedRows(allocResp.response);
    }
  };
  const handleSave = async () => {
    // No second Save while a row write is out. After a successful write, only the reload has a time bound.
    if (!parsedRows.length || isMappingBusyNow() || isUploadSavingRef.current || rowWritesInFlight.has(rebuildKey) || store.allocations.postSubmeterBillingAllocationsSaveAsync.inProgress) return;
    const savedUpload = {
      scope: uploadScope,
      parseCsvResponse
    };
    if (blockIfReadsMissing(parsedRows) || isSingleReading && blockIfSingleReadingConflicts(parsedRows) || blockIfEndBelowStart(parsedRows) || blockIfNegativeReads(parsedRows) || blockIfReadsAboveMax(parsedRows)) return;
    track(events.clicked('Add Bill To Meter'), {
      page,
      rowsCount: parsedRows.length
    });
    const saveRows = toSaveRows(parsedRows);
    closeOpenEditors();
    isUploadSavingRef.current = true;
    setIsUploadSaving(true);
    const thisSave = ++saveSeqRef.current;
    const uploadSeqAtSave = uploadSeqRef.current;
    const gridEditSeqAtSave = gridEditSeqRef.current;
    // A write keeps Save and the grid locked until it answers or fails, however long it takes. The reload that follows
    // a successful write gets its own bound; a late reload follows the same supersede rule as one that answers in time.
    let boundTimer;
    const afterSave = () => {
      onRowsSaved === null || onRowsSaved === void 0 || onRowsSaved();
      loadVariance();
      loadUnmappedUnits();
    };
    // The reload answers with the rows this Save sent. It is ignored, whenever it answers, when something newer has
    // happened since the click: another Save, a new upload, a committed edit to the upload grid, or a cell editor
    // holding text that differs from its cell, none of which it may overwrite. With nothing newer it is applied, inside
    // the bound or after it. The grid is read only while the lock is held, so only an edit made after the bound can
    // supersede.
    const isSuperseded = () => saveSeqRef.current !== thisSave || uploadSeqRef.current !== uploadSeqAtSave || gridEditSeqRef.current !== gridEditSeqAtSave || hasTypedEdit();
    try {
      const resp = await trackRowWrite(rebuildKey, () => store.allocations.postSubmeterBillingAllocationsSaveAsync.run({
        propertyId,
        utilityId,
        billingCycleId,
        allocationCycleId,
        userInitialCsvFileId: (parseCsvResponse === null || parseCsvResponse === void 0 ? void 0 : parseCsvResponse.csvFileId) || '',
        userEditedCsvFileId: (parseCsvResponse === null || parseCsvResponse === void 0 ? void 0 : parseCsvResponse.csvFileId) || '',
        acknowledgeAppr: true,
        cycleDisclosure: (parseCsvResponse === null || parseCsvResponse === void 0 ? void 0 : parseCsvResponse.cycleDisclosure) || {
          providerPhone: '',
          billingAgentDisclosure: '',
          leakServiceContact: ''
        },
        rows: saveRows
      }));
      if (!(resp !== null && resp !== void 0 && resp.isSuccessful)) return;
      toast('success', {
        body: ['Allocations saved!']
      });
      // Saved now: the server holds it, and a return to this tab reads it from there. Only the upload this Save sent.
      onUnsavedUploadSaved === null || onUnsavedUploadSaved === void 0 || onUnsavedUploadSaved(savedUpload);
      savedParseRef.current = parseCsvResponse;
      const gaveUp = new Promise(resolve => {
        boundTimer = setTimeout(() => resolve('timeout'), SAVE_RELOAD_TIMEOUT_MS);
      });
      const outcome = await Promise.race([reloadRows(isSuperseded).then(() => 'done'), gaveUp]);
      if (outcome === 'timeout' && isActiveRef.current) {
        toast('warning', {
          body: ['Allocations saved, but the saved rows did not load in time. Open this tab again to see them.']
        });
      }
      afterSave();
    } finally {
      clearTimeout(boundTimer);
      isUploadSavingRef.current = false;
      if (isActiveRef.current) setIsUploadSaving(false);
    }
  };
  const columns = [{
    field: 'unitName',
    headerName: 'Unit',
    width: 80,
    cellClassName: 'cell-readonly',
    sortComparator: (v1, v2) => ('' + v1).localeCompare(v2, undefined, {
      numeric: true
    })
  }, {
    field: 'residentFullName',
    headerName: 'Resident',
    flex: 1,
    minWidth: 150,
    cellClassName: 'cell-readonly'
  }, {
    field: 'meterSerialNumber',
    headerName: 'Meter #',
    width: 100,
    cellClassName: 'cell-readonly'
  }, {
    field: 'startMeterDateOnly',
    headerName: 'Start date',
    width: 120,
    editable: true,
    valueFormatter: params => params.value ? moment(params.value).format('MM/DD/YY') : ''
  }, {
    field: 'endMeterDateOnly',
    headerName: 'End date',
    width: 120,
    editable: true,
    valueFormatter: params => params.value ? moment(params.value).format('MM/DD/YY') : ''
  }, _objectSpread({
    field: 'startMeterRead',
    cellClassName: params => isReadInvalid(params.row, params.field) ? 'cell-read-invalid' : '',
    headerName: 'Start read',
    width: isSingleReading ? 150 : 100,
    type: 'number',
    editable: true,
    align: 'left',
    headerAlign: 'left'
  }, isSingleReading && {
    renderCell: params => {
      var _params$formattedValu;
      return isReadEmpty(params.value) ? /*#__PURE__*/React.createElement("span", {
        className: classes.openingNeeded
      }, "Opening read needed") : (_params$formattedValu = params.formattedValue) !== null && _params$formattedValu !== void 0 ? _params$formattedValu : params.value;
    }
  }), ...(isSingleReading ? [{
    field: 'startSource',
    headerName: 'Start source',
    width: 160,
    cellClassName: 'cell-readonly'
  }] : []), {
    field: 'endMeterRead',
    cellClassName: params => isReadInvalid(params.row, params.field) ? 'cell-read-invalid' : '',
    headerName: 'End read',
    width: 100,
    type: 'number',
    editable: true,
    align: 'left',
    headerAlign: 'left'
  }, {
    field: 'meterUsage',
    headerName: 'Usage',
    width: 100,
    type: 'number',
    editable: false,
    align: 'left',
    headerAlign: 'left',
    valueFormatter: params => formatUsage(params.value)
  }, {
    field: 'meterRate',
    headerName: 'Rate',
    width: 80,
    type: 'number',
    editable: true,
    align: 'left',
    headerAlign: 'left',
    cellClassName: params => params.row.isRateOverridden ? 'cell-rate-overridden' : '',
    valueFormatter: params => formatMeterRate(params.value)
  }, {
    field: 'unitsOfMeasure',
    headerName: 'UOM',
    width: 80,
    editable: true
  },
  // Charge and service days are recomputed on save from reads, bill and residency dates, so a
  // typed value here never reached the server. Read-only until the backend accepts them (LB-5837).
  {
    field: 'allocationCharge',
    headerName: 'Charge',
    width: 100,
    type: 'number',
    cellClassName: 'cell-readonly',
    align: 'left',
    headerAlign: 'left',
    valueFormatter: params => formatMoney(params.value)
  }, {
    field: 'serviceDaysInPeriod',
    headerName: 'Service days',
    width: 110,
    type: 'number',
    cellClassName: 'cell-readonly',
    align: 'left',
    headerAlign: 'left'
  }];
  const finalRows = (savedAllocations || []).map((row, idx) => {
    var _row$id, _row$residencyId, _row$overrideRate, _row$overrideRate2, _row$unitId, _row$unitName3, _row$residentFullName2, _row$meterSerialNumbe3, _row$startMeterRead2, _row$endMeterRead2, _row$startMeterDateOn2, _row$endMeterDateOnly2, _row$meterUsage2, _row$meterRate2, _row$baseCharge, _row$usageCharge, _row$allocationCharge2, _row$serviceDaysInPer2, _row$unitsOfMeasure2;
    return {
      id: idx,
      allocationId: (_row$id = row.id) !== null && _row$id !== void 0 ? _row$id : '',
      residencyId: (_row$residencyId = row.residencyId) !== null && _row$residencyId !== void 0 ? _row$residencyId : '',
      overrideRate: (_row$overrideRate = row.overrideRate) !== null && _row$overrideRate !== void 0 ? _row$overrideRate : null,
      isRateOverridden: ((_row$overrideRate2 = row.overrideRate) !== null && _row$overrideRate2 !== void 0 ? _row$overrideRate2 : null) !== null,
      unitId: (_row$unitId = row.unitId) !== null && _row$unitId !== void 0 ? _row$unitId : '',
      unitName: (_row$unitName3 = row.unitName) !== null && _row$unitName3 !== void 0 ? _row$unitName3 : '',
      residentFullName: (_row$residentFullName2 = row.residentFullName) !== null && _row$residentFullName2 !== void 0 ? _row$residentFullName2 : '',
      meterSerialNumber: (_row$meterSerialNumbe3 = row.meterSerialNumber) !== null && _row$meterSerialNumbe3 !== void 0 ? _row$meterSerialNumbe3 : '',
      startMeterRead: (_row$startMeterRead2 = row.startMeterRead) !== null && _row$startMeterRead2 !== void 0 ? _row$startMeterRead2 : '',
      endMeterRead: (_row$endMeterRead2 = row.endMeterRead) !== null && _row$endMeterRead2 !== void 0 ? _row$endMeterRead2 : '',
      startMeterDateOnly: (_row$startMeterDateOn2 = row.startMeterDateOnly) !== null && _row$startMeterDateOn2 !== void 0 ? _row$startMeterDateOn2 : '',
      endMeterDateOnly: (_row$endMeterDateOnly2 = row.endMeterDateOnly) !== null && _row$endMeterDateOnly2 !== void 0 ? _row$endMeterDateOnly2 : '',
      meterUsage: (_row$meterUsage2 = row.meterUsage) !== null && _row$meterUsage2 !== void 0 ? _row$meterUsage2 : '',
      meterRate: (_row$meterRate2 = row.meterRate) !== null && _row$meterRate2 !== void 0 ? _row$meterRate2 : '',
      baseCharge: (_row$baseCharge = row.baseCharge) !== null && _row$baseCharge !== void 0 ? _row$baseCharge : '',
      usageCharge: (_row$usageCharge = row.usageCharge) !== null && _row$usageCharge !== void 0 ? _row$usageCharge : '',
      allocationCharge: (_row$allocationCharge2 = row.allocationCharge) !== null && _row$allocationCharge2 !== void 0 ? _row$allocationCharge2 : '',
      serviceDaysInPeriod: (_row$serviceDaysInPer2 = row.serviceDaysInPeriod) !== null && _row$serviceDaysInPer2 !== void 0 ? _row$serviceDaysInPer2 : '',
      unitsOfMeasure: (_row$unitsOfMeasure2 = row.unitsOfMeasure) !== null && _row$unitsOfMeasure2 !== void 0 ? _row$unitsOfMeasure2 : ''
    };
  });
  const processFinalRowUpdate = (rawRow, oldRow) => {
    var _oldRow$overrideRate2, _utilityRef$current, _derivedGroup$find;
    const _resolveRateEdit2 = resolveRateEdit(oldRow.meterRate, rawRow.meterRate, oldRow.isRateOverridden),
      isValid = _resolveRateEdit2.isValid;
    if (!isValid) {
      toast('error', {
        body: ['A rate override has to be a number of zero or more and below 1,000,000,000.']
      });
      return oldRow;
    }
    const rateEdited = rawRow.meterRate !== oldRow.meterRate;
    const overrideRate = rateEdited ? toOverrideRate(rawRow.meterRate) : (_oldRow$overrideRate2 = oldRow.overrideRate) !== null && _oldRow$overrideRate2 !== void 0 ? _oldRow$overrideRate2 : null;
    // A read edit makes Usage the new end minus start, as on the uploaded grid, so the charge
    // derivation below sees a read change and reprices, and splits a shared meter, off the new reads.
    // A read left empty gives an empty Usage, never 0. The Usage cell is read only, so Usage changes
    // only here, with the reads.
    // Reads are held at the four decimals the backend stores, so the Usage derived here is the one a
    // later Recalculate derives from the stored reads. An empty read stays empty.
    const reads = {
      startMeterRead: toStoredRead(rawRow.startMeterRead),
      endMeterRead: toStoredRead(rawRow.endMeterRead)
    };
    const readsChanged = haveReadsChanged(reads, oldRow);
    const newRow = _objectSpread(_objectSpread(_objectSpread({}, rawRow), reads), {}, {
      meterRate: rateEdited ? overrideRate !== null && overrideRate !== void 0 ? overrideRate : oldRow.meterRate : rawRow.meterRate,
      overrideRate,
      isRateOverridden: overrideRate !== null
    }, readsChanged && {
      meterUsage: toUsageFromReads(reads.startMeterRead, reads.endMeterRead)
    });
    track(events.changed('Saved Meter Row'), {
      page,
      unit: newRow.unitName
    });
    // Raw savedAllocations rows can carry meterSerialNumber as null; the grid's own rows always
    // coalesce it to ''. Comparing the two shapes directly leaves the edited row's raw copy out of
    // its own group, so both sides are normalised the same way here before comparing.
    const meterKey = row => {
      var _row$meterSerialNumbe4;
      return "".concat(row.unitId, "::").concat((_row$meterSerialNumbe4 = row.meterSerialNumber) !== null && _row$meterSerialNumbe4 !== void 0 ? _row$meterSerialNumbe4 : '');
    };
    const meterGroup = (savedAllocations || []).map((row, idx) => _objectSpread(_objectSpread({}, row), {}, {
      id: idx,
      allocationId: row.id
    })).filter(row => meterKey(row) === meterKey(newRow));
    const bill = (_utilityRef$current = utilityRef.current) === null || _utilityRef$current === void 0 ? void 0 : _utilityRef$current.bill;
    const periodDays = toPeriodDays(bill === null || bill === void 0 ? void 0 : bill.startDateOnly, bill === null || bill === void 0 ? void 0 : bill.endDateOnly);
    const derivedGroup = deriveMeterGroupCharges(newRow, oldRow, meterGroup, {
      periodDays
    });
    setSavedAllocations(prev => prev.map((row, idx) => {
      const match = derivedGroup.find(d => d.id === idx);
      return match ? _objectSpread(_objectSpread(_objectSpread({}, row), match), {}, {
        id: row.id
      }) : row;
    }));
    return (_derivedGroup$find = derivedGroup.find(d => d.id === newRow.id)) !== null && _derivedGroup$find !== void 0 ? _derivedGroup$find : newRow;
  };
  const persistRows = async () => {
    const resp = await trackRowWrite(rebuildKey, () => store.allocations.putSubmeterBillingAllocationsAsync.run({
      rows: toUpdateRows(savedAllocations)
    }));
    if (!(resp !== null && resp !== void 0 && resp.isSuccessful)) return false;
    await reloadRows();
    onRowsSaved === null || onRowsSaved === void 0 || onRowsSaved();
    return true;
  };
  const handleFinalSave = async () => {
    if (!(savedAllocations !== null && savedAllocations !== void 0 && savedAllocations.length) || isMappingBusyNow()) return;
    if (blockIfReadsMissing(savedAllocations) || blockIfEndBelowStart(savedAllocations) || blockIfNegativeReads(savedAllocations) || blockIfReadsAboveMax(savedAllocations) || blockIfUsageMismatch(savedAllocations)) return;
    track(events.clicked('Save Meter Allocations'), {
      page,
      rowsCount: savedAllocations.length
    });
    setIsSaving(true);
    try {
      if (await persistRows()) {
        toast('success', {
          body: ['Allocations updated!']
        });
        loadUnmappedUnits();
        await loadVariance();
      }
    } finally {
      setIsSaving(false);
    }
  };
  const reviewStore = store.allocations.postSubmeterBillingAllocationsReviewAsync;
  const approveStore = store.allocations.postSubmeterBillingAllocationsApproveAsync;
  const handleReview = async checked => {
    if (!checked || !variance || isReviewing) return;
    track(events.enabled('Variance Reviewed'), {
      page
    });
    const epoch = cycleEpochRef.current;
    setIsReviewing(true);
    try {
      var _reviewStore$error;
      const resp = await reviewStore.run({
        propertyId,
        utilityId,
        billingCycleId,
        draftRevision: variance.draftRevision
      });
      if (resp !== null && resp !== void 0 && resp.isSuccessful) {
        if (epoch === cycleEpochRef.current) setVariance(prev => _objectSpread(_objectSpread({}, prev), {}, {
          isReviewed: true
        }));
      } else if (((_reviewStore$error = reviewStore.error) === null || _reviewStore$error === void 0 ? void 0 : _reviewStore$error.errorCode) === DRAFT_CHANGED) {
        await loadVariance();
      } else {
        toastFailure(reviewStore);
      }
    } finally {
      setIsReviewing(false);
    }
  };
  const approveAndSave = async () => {
    const epoch = cycleEpochRef.current;
    const billVersion = billVersionRef.current;
    setIsApproveFlowRunning(true);
    try {
      var _approveStore$error;
      track(events.clicked('Approve And Save Meter Allocations'), {
        page
      });
      if (!(savedAllocations !== null && savedAllocations !== void 0 && savedAllocations.length) || !(await persistRows())) return;

      // A row change drops a stored review, so approve is decided from a fresh read.
      const fresh = await loadVariance();
      if (!fresh || fresh.requiresAcknowledgement && !fresh.isReviewed) return;
      if (!isActiveRef.current || billVersion !== billVersionRef.current) return;
      const resp = await approveStore.run({
        propertyId,
        utilityId,
        billingCycleId
      });
      if (resp !== null && resp !== void 0 && resp.isSuccessful) {
        if (epoch === cycleEpochRef.current && billVersion === billVersionRef.current) setIsApproved(true);
        toast('success', {
          body: ['Allocations approved!']
        });
        onApproved === null || onApproved === void 0 || onApproved();
        return;
      }
      if (((_approveStore$error = approveStore.error) === null || _approveStore$error === void 0 ? void 0 : _approveStore$error.errorCode) === ACKNOWLEDGEMENT_REQUIRED) {
        await loadVariance();
      } else {
        toastFailure(approveStore);
      }
    } finally {
      setIsApproveFlowRunning(false);
    }
  };
  const withNoMapping = (_unmappedUnits$filter = unmappedUnits === null || unmappedUnits === void 0 ? void 0 : unmappedUnits.filter(unit => !unit.isMapped)) !== null && _unmappedUnits$filter !== void 0 ? _unmappedUnits$filter : [];
  const unmappedSummary = withNoMapping.length ? "".concat(withNoMapping.length, " billable ").concat(withNoMapping.length === 1 ? 'unit has' : 'units have', " no mapping and will not be charged: ").concat(withNoMapping.map(unit => unit.unitName || 'Unnamed unit').join(', '), ".") : null;
  const unmappedNotice = isBillRemoved ? null : unmappedSummary;
  const isRowsLoadFailedView = rowsLoadFailed && !savedAllocations && (isCycleApproved || mustConfirmRows);
  const isStaleView = isRecalculatedStale && !savedAllocations;
  // Before a file is read there are no rows, and every billable unit would read as missing. The two views that hide the
  // grid could hold an earlier upload in parsedRows, so they warn about nothing.
  const gridRows = savedAllocations !== null && savedAllocations !== void 0 ? savedAllocations : parsedRows;
  const unitsWithRows = new Set(gridRows.map(row => row.unitId));
  const unitsWithNoReads = isBillRemoved || isRowsLoadFailedView || isStaleView || !gridRows.length ? [] : (unmappedUnits !== null && unmappedUnits !== void 0 ? unmappedUnits : []).filter(unit => unit.isMapped && !unitsWithRows.has(unit.unitId));
  const isOtherWorkRunning = () => isMappingBusyNow() || store.allocations.putSubmeterBillingAllocationsAsync.inProgress || store.allocations.postSubmeterBillingAllocationsSaveAsync.inProgress || store.allocations.postSubmeterBillingAllocationsClearAsync.inProgress || approveStore.inProgress || reviewStore.inProgress || rebuildsInFlight.has(rebuildKey) || rowWritesInFlight.has(rebuildKey) || approvalsInFlight.has(rebuildKey);
  const startApproval = () => trackApproval(rebuildKey, approveAndSave);
  const liveBillKey = () => {
    var _utilityRef$current2;
    const bill = (_utilityRef$current2 = utilityRef.current) === null || _utilityRef$current2 === void 0 ? void 0 : _utilityRef$current2.bill;
    return "".concat(bill === null || bill === void 0 ? void 0 : bill.id, ":").concat(bill === null || bill === void 0 ? void 0 : bill.startDateOnly, ":").concat(bill === null || bill === void 0 ? void 0 : bill.endDateOnly);
  };
  const isCycleClosedNow = () => {
    var _store$homeDashboardS8;
    const status = (_store$homeDashboardS8 = store.homeDashboardStore.getCurrentCompany) === null || _store$homeDashboardS8 === void 0 || (_store$homeDashboardS8 = _store$homeDashboardS8.billingTab) === null || _store$homeDashboardS8 === void 0 || (_store$homeDashboardS8 = _store$homeDashboardS8.applyBill) === null || _store$homeDashboardS8 === void 0 ? void 0 : _store$homeDashboardS8.status;
    const liveUtility = utilityRef.current;
    return status === 'TableReady' || status === 'TableReadyStatementSent' || !!liveUtility && !liveUtility.bill;
  };
  const canApproveNow = () => {
    const live = liveCycleRef.current;
    return isActiveRef.current && !isCycleClosedNow() && !live.isBillRemoved && !live.isCycleApproved && !live.isStatementSent && !live.isWorkRunning && !isOtherWorkRunning();
  };
  const approvalContext = () => "".concat(cycleEpochRef.current, ":").concat(billVersionRef.current, ":").concat(bindGenerationRef.current, ":").concat(liveBillKey());
  const askAboutUnmappedUnits = async context => {
    setIsCheckingUnmapped(true);
    try {
      const units = await settledUnmappedUnits();
      if (context !== approvalContext() || !canApproveNow()) return;
      if (units !== null && units !== void 0 && units.some(unit => !unit.isMapped)) {
        unmappedContextRef.current = context;
        setIsUnmappedConfirmOpen(true);
        return;
      }
      startApproval();
    } finally {
      setIsCheckingUnmapped(false);
    }
  };
  const handleConfirmUnmapped = () => {
    if (!isUnmappedConfirmOpen) return undefined;
    setIsUnmappedConfirmOpen(false);
    const openedIn = unmappedContextRef.current;
    unmappedContextRef.current = null;
    if (openedIn !== approvalContext() || !canApproveNow()) return undefined;
    return startApproval();
  };
  useEffect(() => {
    setIsUnmappedConfirmOpen(false);
  }, [billKey, isBillRemoved, isCycleApproved, isStatementSent]);
  useEffect(() => {
    if (!unmappedNotice) setIsUnmappedConfirmOpen(false);
  }, [unmappedNotice]);

  // LB-6030: warns again before approving a long-past service period, but records nothing; the confirmation kept is
  // the one given when it was allocated.
  const handleApproveAndSave = () => {
    if (blockIfReadsMissing(savedAllocations || []) || blockIfEndBelowStart(savedAllocations || []) || blockIfNegativeReads(savedAllocations || []) || blockIfReadsAboveMax(savedAllocations || []) || blockIfUsageMismatch(savedAllocations || [])) return;
    const context = approvalContext();
    oldServicePeriod.confirmIfOld([servicePeriod], () => askAboutUnmappedUnits(context), {
      confirmLabel: 'Approve & save',
      title: 'Approve a bill for a long-past period?'
    });
  };

  // These writes outlive a closed screen, so a reopened screen reads their locks from the store.
  const isStoreWriteRunning = store.allocations.putSubmeterBillingAllocationsAsync.inProgress || store.allocations.postSubmeterBillingAllocationsSaveAsync.inProgress || store.allocations.postSubmeterBillingAllocationsClearAsync.inProgress || approveStore.inProgress || reviewStore.inProgress;
  const isGridLocked = isMappingBusy || isSaving || isApproveFlowRunning || isCheckingUnmapped || isStoreWriteRunning || isRecalculating || isRefreshingRecalculatedRows || isRecalculatedStale || isBillRemoved || isCycleApproved;
  liveCycleRef.current = {
    isBillRemoved,
    isCycleApproved,
    isStatementSent,
    isWorkRunning: isSaving || isApproveFlowRunning || isRefreshingRecalculatedRows || isRecalculatedStale
  };
  const readSavedRows = async () => {
    const resp = await store.allocations.getSubmeterBillingAllocationsAsync.run({
      propertyId,
      utilityId,
      billingCycleId
    });
    return resp !== null && resp !== void 0 && resp.isSuccessful && Array.isArray(resp.response) ? resp.response : null;
  };
  const showNoRows = () => {
    setSavedAllocations(null);
    setParsedRows([]);
    setParseCsvResponse(null);
    setParseWarnings([]);
    resetPendingOpenings();
    onNoRows === null || onNoRows === void 0 || onNoRows();
    loadUnmappedUnits();
  };
  const showRecalculatedRows = async () => {
    if (!isActiveRef.current) return;
    const fresh = await readSavedRows();
    if (!fresh) {
      loadUnmappedUnits();
      setIsRecalculatedStale(true);
      return;
    }
    setIsRecalculatedStale(false);
    if (!fresh.length) {
      varianceReadIdRef.current += 1;
      setVariance(null);
      setIsVarianceLoading(false);
      setIsVarianceFailed(false);
      showNoRows();
      toast('success', {
        body: ['Allocations recalculated. No units are left to bill for this cycle.']
      });
      return;
    }
    applyLoadedRows(fresh);
    toast('success', {
      body: ['Allocations recalculated!']
    });
    onRowsSaved === null || onRowsSaved === void 0 || onRowsSaved();
    loadUnmappedUnits();
    await loadVariance();
  };
  const runRecalculateStep = async step => {
    if (isSaving || isApproveFlowRunning || isStoreWriteRunning || isRefreshingRecalculatedRows || isCycleApproved || rebuildsInFlight.has(rebuildKey)) {
      return;
    }
    ranRebuildHereRef.current = true;
    setRebuildInFlight(rebuildKey, true);
    try {
      await step();
    } finally {
      setRebuildInFlight(rebuildKey, false);
    }
  };
  useEffect(() => {
    const finished = wasRecalculatingRef.current && !isRecalculating;
    wasRecalculatingRef.current = isRecalculating;
    // Keep the lock through the row refresh, including the render where the remote rebuild finishes.
    if (isRecalculating && !ranRebuildHereRef.current) setIsRefreshingRecalculatedRows(true);
    if (!finished) return;
    if (ranRebuildHereRef.current) {
      ranRebuildHereRef.current = false;
      return;
    }
    // This read cancels a mount read still in flight; the new epoch makes that read's empty result ignored.
    cycleEpochRef.current += 1;
    const epoch = cycleEpochRef.current;
    readSavedRows().then(fresh => {
      if (epoch !== cycleEpochRef.current) return;
      setInitialLoading(false);
      if (!fresh) {
        loadUnmappedUnits();
        setIsRecalculatedStale(true);
        return;
      }
      setIsRecalculatedStale(false);
      if (fresh.length) {
        applyLoadedRows(fresh);
        loadVariance();
        loadUnmappedUnits();
        return;
      }
      showNoRows();
    }).finally(() => {
      if (epoch === cycleEpochRef.current && !rebuildsInFlight.has(rebuildKey)) setIsRefreshingRecalculatedRows(false);
    });
  }, [isRecalculating, rebuildKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirmRecalculate = () => {
    setIsRecalculateConfirmOpen(false);
    return runRecalculateStep(async () => {
      track(events.clicked('Confirm Recalculate Meter Allocations'), {
        page
      });
      const current = await readSavedRows();
      if (!current) {
        // A failed read has already raised the store's error toast; a cancelled one raises nothing.
        if (!store.allocations.getSubmeterBillingAllocationsAsync.error) {
          toast('error', {
            body: ['The saved rows could not be read, so nothing was recalculated. Try again.']
          });
        }
        return;
      }
      if (!current.length) {
        toast('error', {
          body: ['There are no saved rows to recalculate.']
        });
        return;
      }
      const divergent = findDivergentMeter(current);
      if (divergent) {
        toast('error', {
          body: ["Unit ".concat(divergent.unitName, ", meter ").concat(divergent.meterSerialNumber || '(none)', " has different saved reads on its rows. Make them match and save, then recalculate.")]
        });
        return;
      }
      const resp = await store.allocations.postSubmeterBillingAllocationsSaveAsync.run(toRecalculateRequest(current, {
        propertyId,
        utilityId,
        billingCycleId,
        allocationCycleId
      }));
      if (!(resp !== null && resp !== void 0 && resp.isSuccessful)) return;
      await showRecalculatedRows();
    });
  };
  const handleReloadRecalculatedRows = () => runRecalculateStep(showRecalculatedRows);
  const handleConfirmClear = async () => {
    setIsClearConfirmOpen(false);
    if (isOtherWorkRunning()) return;
    const resp = await trackRowWrite(rebuildKey, () => store.allocations.postSubmeterBillingAllocationsClearAsync.run({
      propertyId,
      utilityId,
      billingCycleId
    }));
    if (!(resp !== null && resp !== void 0 && resp.isSuccessful)) return;
    toast('success', {
      body: ['Saved reads cleared.']
    });
    onCleared === null || onCleared === void 0 || onCleared();
    if (!isActiveRef.current) return;
    // A save's reload or a variance read still in flight would put the cleared rows back.
    saveSeqRef.current += 1;
    uploadSeqRef.current += 1;
    varianceReadIdRef.current += 1;
    savedParseRef.current = null;
    setVariance(null);
    setIsVarianceLoading(false);
    setIsVarianceFailed(false);
    setBalancedMeters(new Set());
    resetUpload();
    showNoRows();
  };
  const isRebuildPending = isRecalculating || isRefreshingRecalculatedRows || isRecalculatedStale;
  useEffect(() => {
    onBusy === null || onBusy === void 0 || onBusy(isRebuildPending);
    return () => onBusy === null || onBusy === void 0 ? void 0 : onBusy(false);
  }, [isRebuildPending]); // eslint-disable-line react-hooks/exhaustive-deps

  const isVarianceBusy = isVarianceLoading || isReviewing || isGridLocked;
  const splitShares = toSplitRowShares(finalRows, {
    splitCents: splitCentsByDays,
    compareRows: byResidencyThenRow
  });
  const shownValue = (field, id, value) => {
    var _splitShares$get$fiel, _splitShares$get;
    return (_splitShares$get$fiel = (_splitShares$get = splitShares.get(id)) === null || _splitShares$get === void 0 ? void 0 : _splitShares$get[field]) !== null && _splitShares$get$fiel !== void 0 ? _splitShares$get$fiel : value;
  };
  const showShare = (field, params) => formatMoney(shownValue(field, params.id, params.value));
  const sortByShare = field => (v1, v2, cell1, cell2) => gridNumberComparator(shownValue(field, cell1.id, v1), shownValue(field, cell2.id, v2), cell1, cell2);
  const isShareCell = _ref13 => {
    var _splitShares$get2;
    let id = _ref13.id,
      field = _ref13.field;
    return ((_splitShares$get2 = splitShares.get(id)) === null || _splitShares$get2 === void 0 ? void 0 : _splitShares$get2[field]) !== undefined;
  };
  const _ref14 = (_utility$bill6 = utility === null || utility === void 0 ? void 0 : utility.bill) !== null && _utility$bill6 !== void 0 ? _utility$bill6 : {},
    billStart = _ref14.startDateOnly,
    billEnd = _ref14.endDateOnly;
  const billDays = billStart && billEnd ? moment(billEnd).diff(moment(billStart), 'days') + 1 : 0;
  const proration = toProration(finalRows, billDays);
  const finalColumns = [{
    field: 'unitName',
    headerName: 'Unit',
    width: 80,
    sortComparator: (v1, v2) => ('' + v1).localeCompare(v2, undefined, {
      numeric: true
    })
  }, {
    field: 'residentFullName',
    headerName: 'Resident',
    flex: 1,
    minWidth: 150
  }, {
    field: 'meterSerialNumber',
    headerName: 'Meter #',
    width: 100
  }, {
    field: 'startMeterDateOnly',
    headerName: 'Start date',
    width: 120,
    valueFormatter: params => params.value ? moment(params.value).format('MM/DD/YY') : ''
  }, {
    field: 'endMeterDateOnly',
    headerName: 'End date',
    width: 120,
    valueFormatter: params => params.value ? moment(params.value).format('MM/DD/YY') : ''
  }, {
    field: 'startMeterRead',
    cellClassName: params => isReadInvalid(params.row, params.field) ? 'cell-read-invalid' : '',
    headerName: 'Start read',
    width: 100,
    type: 'number',
    editable: true,
    align: 'left',
    headerAlign: 'left'
  }, {
    field: 'endMeterRead',
    cellClassName: params => isReadInvalid(params.row, params.field) ? 'cell-read-invalid' : '',
    headerName: 'End read',
    width: 100,
    type: 'number',
    editable: true,
    align: 'left',
    headerAlign: 'left'
  }, {
    field: 'meterUsage',
    headerName: 'Usage',
    width: 100,
    type: 'number',
    editable: false,
    align: 'left',
    headerAlign: 'left',
    valueFormatter: params => formatUsage(params.value)
  }, {
    field: 'meterRate',
    headerName: 'Rate',
    width: 80,
    type: 'number',
    editable: true,
    align: 'left',
    headerAlign: 'left',
    cellClassName: params => params.row.isRateOverridden ? 'cell-rate-overridden' : '',
    valueFormatter: params => formatMeterRate(params.value)
  }, {
    field: 'unitsOfMeasure',
    headerName: 'UOM',
    width: 80
  }, {
    field: 'baseCharge',
    headerName: 'Base charge',
    width: 110,
    type: 'number',
    editable: true,
    align: 'left',
    headerAlign: 'left',
    valueFormatter: params => showShare('baseCharge', params),
    sortComparator: sortByShare('baseCharge')
  }, {
    field: 'usageCharge',
    headerName: 'Usage charge',
    width: 120,
    type: 'number',
    editable: true,
    align: 'left',
    headerAlign: 'left',
    valueFormatter: params => showShare('usageCharge', params),
    sortComparator: sortByShare('usageCharge')
  }, {
    field: 'allocationCharge',
    headerName: 'Total charge',
    width: 120,
    type: 'number',
    editable: true,
    align: 'left',
    headerAlign: 'left',
    valueFormatter: params => formatMoney(params.value)
  }, ...(proration.days.size ? [{
    field: 'prorated',
    headerName: 'Prorated',
    width: 140,
    sortable: false,
    valueGetter: params => proration.days.has(params.id) ? "".concat(proration.days.get(params.id), " of ").concat(billDays, " days") : ''
  }] : []), {
    field: 'serviceDaysInPeriod',
    headerName: 'Service days',
    width: 110,
    type: 'number',
    editable: true,
    align: 'left',
    headerAlign: 'left',
    // The backend stores whole days; a fraction or a negative stays in the cell, flagged, uncommitted.
    preProcessEditCellProps: params => {
      const value = params.props.value;
      const isWholeDays = value == null || value === '' || Number.isInteger(Number(value)) && Number(value) >= 0;
      return _objectSpread(_objectSpread({}, params.props), {}, {
        error: !isWholeDays
      });
    }
  }];
  const unmappedAlert = /*#__PURE__*/React.createElement(React.Fragment, null, unmappedNotice && /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    sx: {
      mb: 2
    }
  }, unmappedNotice), unitsWithNoReads.map(unit => /*#__PURE__*/React.createElement(Alert, {
    key: unit.unitId,
    severity: "warning",
    sx: {
      mb: 2
    }
  }, "No reads for unit ".concat(unit.unitName || 'Unnamed unit', " (").concat(unit.residents.map(resident => resident.name).join(', '), "): it won't be charged. Add it to the file and upload again."))));
  const staleAlert = isRecalculatedStale ? /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    action: /*#__PURE__*/React.createElement(Button, {
      color: "inherit",
      size: "small",
      onClick: handleReloadRecalculatedRows,
      disabled: isRecalculating || isRefreshingRecalculatedRows
    }, "Reload rows")
  }, "The rows were recalculated, but they could not be refreshed. Reload them to continue.") : null;
  const parseWarningsBlock = /*#__PURE__*/React.createElement(React.Fragment, null, isMappingCheckFailed && /*#__PURE__*/React.createElement(Alert, {
    severity: "error",
    action: /*#__PURE__*/React.createElement(Button, {
      color: "inherit",
      size: "small",
      onClick: () => refreshMapping(),
      disabled: mappingCheckCount > 0 || isMappingWriteRunning
    }, "Retry mapping check")
  }, "Could not verify the current mapping. Check it again before saving."), /*#__PURE__*/React.createElement(ParseWarnings, {
    warnings: shownWarnings,
    onFixMapping: canFixMapping ? handleFixMapping : undefined,
    isFixMappingLoading: isOpeningMapping
  }), isMappingOpen && /*#__PURE__*/React.createElement(OldUtilityConfigurationModal, {
    isOpen: true,
    handleClose: handleMappingClosed,
    utility: (currentPropertyDetails === null || currentPropertyDetails === void 0 ? void 0 : currentPropertyDetails.getUtilityTemplateModalForm) || null
  }));
  if (initialLoading) {
    return /*#__PURE__*/React.createElement("div", {
      className: "".concat(classes.wrapper, " ").concat(classes.submitterWrapper)
    }, /*#__PURE__*/React.createElement(BackdropLoader, {
      isOpen: true
    }));
  }

  // After another screen's row write, a failed read leaves it unknown whether the cycle has rows, and an
  // upload would replace any it has, so only Retry is offered until a read succeeds.
  if (isRowsLoadFailedView) {
    return /*#__PURE__*/React.createElement("div", {
      className: "".concat(classes.wrapper, " ").concat(classes.submitterWrapper)
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.header
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "h4"
    }, "Meter details")), /*#__PURE__*/React.createElement(Alert, {
      severity: "error",
      action: /*#__PURE__*/React.createElement(Button, {
        color: "inherit",
        size: "small",
        onClick: loadSavedRows
      }, "Retry")
    }, "The meter rows could not be loaded."), unmappedAlert);
  }
  if (isStaleView) {
    return /*#__PURE__*/React.createElement("div", {
      className: "".concat(classes.wrapper, " ").concat(classes.submitterWrapper)
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.header
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "h4"
    }, "Meter details")), staleAlert, unmappedAlert);
  }
  if (savedAllocations) {
    var _utility$bill7;
    return /*#__PURE__*/React.createElement("div", {
      className: "".concat(classes.wrapper, " ").concat(classes.submitterWrapper)
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.header
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Typography, {
      variant: "h4"
    }, "Meter details"), /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      className: classes.subtitle
    }, "Allocations saved successfully.")), !isStatementSent && !isCycleApproved && !isBillRemoved && (utility === null || utility === void 0 ? void 0 : utility.isEditable) && /*#__PURE__*/React.createElement(Button, {
      variant: "actionButtonInvert",
      onClick: () => setIsClearConfirmOpen(true),
      disabled: isGridLocked || isReviewing
    }, "Clear reads")), isBillRemoved && /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      role: "status"
    }, NO_BILL_NOTICE), parseWarningsBlock, unmappedAlert, /*#__PURE__*/React.createElement("div", {
      className: classes.tableWrapper
    }, /*#__PURE__*/React.createElement(DataGrid, {
      key: isBillRemoved ? 'read-only' : 'editable',
      autoHeight: true,
      columns: finalColumns,
      rows: finalRows,
      processRowUpdate: processFinalRowUpdate,
      isCellEditable: params => !isGridLocked && !isShareCell(params),
      onProcessRowUpdateError: () => toast('error', {
        body: ['That change could not be applied.']
      }),
      disableRowSelectionOnClick: true,
      hideFooter: true,
      columnHeaderHeight: 60,
      rowHeight: 40,
      initialState: {
        sorting: {
          sortModel: [{
            field: 'unitName',
            sort: 'asc'
          }]
        }
      },
      sx: {
        '& .MuiDataGrid-cell': {
          fontWeight: 400
        },
        '& .cell-rate-overridden': {
          backgroundColor: palette.info.light,
          color: palette.info.dark,
          borderLeft: "3px solid ".concat(palette.info.main)
        },
        '& .cell-read-invalid': {
          backgroundColor: 'error.light',
          color: 'error.dark',
          borderLeft: 3,
          borderColor: 'error.main'
        }
      }
    })), /*#__PURE__*/React.createElement(MeterTotals, {
      rows: finalRows,
      shares: splitShares,
      bill: (_utility$bill7 = utility === null || utility === void 0 ? void 0 : utility.bill) !== null && _utility$bill7 !== void 0 ? _utility$bill7 : {},
      ownerCents: proration.ownerCents
    }), renderInvalidReadsWarning(savedAllocations || []), renderUsageWarning(savedMeteredUnits), renderUsageUnitNotes(savedAllocations, savedMeteredUnits), renderMeterTotalWarnings(finalRows), staleAlert, !isStatementSent && !isBillRemoved && /*#__PURE__*/React.createElement(VarianceReview, {
      variance: variance,
      loadFailed: isVarianceFailed && !isVarianceLoading,
      isApproved: isCycleApproved,
      isBusy: isVarianceBusy,
      onReview: handleReview,
      onRetry: loadVariance
    }), !isStatementSent && !isBillRemoved && /*#__PURE__*/React.createElement("div", {
      className: classes.saveButtonWrapper
    }, !isCycleApproved && /*#__PURE__*/React.createElement(Button, {
      variant: "actionButtonInvert",
      onClick: () => {
        track(events.clicked('Recalculate Meter Allocations'), {
          page
        });
        setIsRecalculateConfirmOpen(true);
      },
      disabled: isGridLocked || isReviewing
    }, isRecalculating ? 'Recalculating...' : 'Recalculate'), /*#__PURE__*/React.createElement(Button, {
      variant: "contained",
      onClick: handleFinalSave,
      disabled: isGridLocked || isReviewing || store.allocations.putSubmeterBillingAllocationsAsync.inProgress
    }, isSaving ? 'Saving...' : 'Save'), /*#__PURE__*/React.createElement(Button, {
      variant: "contained",
      onClick: handleApproveAndSave,
      disabled: isVarianceBusy || !variance || variance.requiresAcknowledgement && !variance.isReviewed
    }, isApproveFlowRunning || isCheckingUnmapped ? 'Approving...' : 'Approve & save')), /*#__PURE__*/React.createElement(SimpleModal, {
      handleClose: () => setIsRecalculateConfirmOpen(false),
      isOpen: isRecalculateConfirmOpen,
      noPadding: true,
      rightButton: /*#__PURE__*/React.createElement(Button, {
        onClick: handleConfirmRecalculate,
        type: "button",
        variant: "contained"
      }, "Recalculate"),
      title: "Recalculate meter amounts?"
    }, /*#__PURE__*/React.createElement(Alert, {
      severity: "warning",
      icon: false
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2"
    }, "Every row is rebuilt from the saved meter reads, the current bill and the current meter mappings. Edits in this grid, including any rate you typed, are discarded. If a unit is no longer mapped on this utility, its charges are removed."))), /*#__PURE__*/React.createElement(SimpleModal, {
      handleClose: () => setIsClearConfirmOpen(false),
      isOpen: isClearConfirmOpen,
      noPadding: true,
      rightButton: /*#__PURE__*/React.createElement(Button, {
        onClick: handleConfirmClear,
        type: "button",
        variant: "contained"
      }, "Clear reads"),
      title: "Clear saved reads?"
    }, /*#__PURE__*/React.createElement(Alert, {
      severity: "warning",
      icon: false
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2"
    }, "This removes the saved reads for every meter in this month. You can then upload a new file. The uploaded file stays on record."))), /*#__PURE__*/React.createElement(SimpleModal, {
      handleClose: () => {
        unmappedContextRef.current = null;
        setIsUnmappedConfirmOpen(false);
      },
      isOpen: isUnmappedConfirmOpen,
      noPadding: true,
      rightButton: /*#__PURE__*/React.createElement(Button, {
        onClick: handleConfirmUnmapped,
        type: "button",
        variant: "contained"
      }, "Approve anyway"),
      title: "Approve with unmapped billable units?"
    }, /*#__PURE__*/React.createElement(Alert, {
      severity: "warning",
      icon: false
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2"
    }, unmappedSummary))), oldServicePeriod.confirmation);
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.wrapper, " ").concat(classes.submitterWrapper)
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.header
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Typography, {
    variant: "h4"
  }, "Meter details"), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.subtitle
  }, isBillRemoved ? NO_BILL_SUBTITLE : 'Enter meter reads manually or upload csv to auto-fill fields.')), !isBillRemoved && !areUploadAreasShown && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    startIcon: /*#__PURE__*/React.createElement(Icon, {
      type: "file-text"
    }),
    onClick: () => {
      var _fileInputRef$current;
      track(events.clicked('Upload CSV Reads'), {
        page
      });
      (_fileInputRef$current = fileInputRef.current) === null || _fileInputRef$current === void 0 || _fileInputRef$current.click();
    },
    disabled: store.allocations.postSubmeterBillingParseCsvAsync.inProgress || isRecalculating || isTemplatePending
  }, store.allocations.postSubmeterBillingParseCsvAsync.inProgress ? 'Parsing...' : 'Upload csv reads'), /*#__PURE__*/React.createElement("input", {
    ref: fileInputRef,
    type: "file",
    accept: ".csv",
    hidden: true,
    onChange: handleCsvUpload
  }))), isBillRemoved && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    role: "status"
  }, NO_BILL_NOTICE), !isBillRemoved && areUploadAreasShown && /*#__PURE__*/React.createElement(ReadsUploadAreas, {
    classes: classes,
    start: {
      title: 'Start reads (beginning of period)',
      mark: startMark,
      help: startHelp,
      status: startFile ? "".concat(startFile.rowCount, " ").concat(startFile.rowCount === 1 ? 'row' : 'rows', " read.") : null,
      buttonLabel: 'Upload start reads',
      inputLabel: 'Start reads file',
      inputRef: startFileInputRef,
      onChange: e => handleCsvUpload(e, 'start'),
      disabled: isParseBusy || isUploadSaving || isTemplatePending
    },
    end: {
      title: 'End reads (end of period)',
      help: 'The reads at the end of the period. This file sets the rows below.',
      status: parseCsvResponse ? "".concat(endRowCount, " ").concat(endRowCount === 1 ? 'row' : 'rows', " read.") : null,
      buttonLabel: 'Upload end reads',
      inputLabel: 'End reads file',
      inputRef: fileInputRef,
      onChange: e => handleCsvUpload(e, 'end'),
      disabled: isParseBusy || isTemplatePending
    }
  }), refusedFile && /*#__PURE__*/React.createElement("div", {
    className: classes.uploadNotice
  }, /*#__PURE__*/React.createElement(Alert, {
    severity: "error",
    sx: {
      mb: 2
    }
  }, toRefusedFileMessage(refusedFile.label)), /*#__PURE__*/React.createElement(ParseWarnings, {
    warnings: refusedFile.warnings
  })), pendingOpenings.length > 0 && /*#__PURE__*/React.createElement("div", {
    className: classes.uploadNotice
  }, /*#__PURE__*/React.createElement(Alert, {
    severity: "info",
    role: "status"
  }, "Start reads not matched to an end read yet (not charged): ".concat(pendingOpenings.map(opening => "unit ".concat(opening.unitName, ", meter ").concat(opening.meterSerialNumber || '(none)')).join('; '), "."))), parseWarningsBlock, unmappedAlert, parsedRows.length === 0 && /*#__PURE__*/React.createElement("div", {
    className: classes.emptyState
  }, /*#__PURE__*/React.createElement("img", {
    src: noDataSvg,
    alt: "No data",
    className: classes.emptyStateIcon
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.emptyStateText
  }, isBillRemoved ? NO_BILL_EMPTY_STATE : areUploadAreasShown ? 'Upload the end reads to continue' : 'Upload a file to continue')), parsedRows.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classes.tableWrapper,
    ref: gridWrapperRef
  }, /*#__PURE__*/React.createElement(DataGrid, {
    key: isBillRemoved ? 'read-only' : 'editable',
    autoHeight: true,
    columns: columns,
    rows: rows,
    processRowUpdate: processRowUpdate,
    isCellEditable: () => !isBillRemoved && !isUploadSaving,
    apiRef: gridApiRef,
    disableRowSelectionOnClick: true,
    hideFooter: true,
    columnHeaderHeight: 60,
    rowHeight: 40,
    initialState: {
      sorting: {
        sortModel: [{
          field: 'unitName',
          sort: 'asc'
        }]
      }
    },
    sx: {
      '& .MuiDataGrid-cell': {
        fontWeight: 400
      },
      '& .cell-readonly': {
        backgroundColor: '#f5f5f5',
        color: '#9e9e9e'
      },
      // An overridden rate is the manager's number rather than the calculated one, so it has
      // to read as edited. Deliberately unlike cell-readonly's grey, which says the opposite:
      // that the cell cannot be typed in at all. The left edge carries the same signal as the
      // fill, so the marker does not rely on colour alone. Weight is not available here:
      // styles.js pins .MuiDataGrid-cell and its descendants to font-weight 400 !important.
      '& .cell-rate-overridden': {
        backgroundColor: palette.info.light,
        color: palette.info.dark,
        borderLeft: "3px solid ".concat(palette.info.main)
      },
      '& .cell-read-invalid': {
        backgroundColor: 'error.light',
        color: 'error.dark',
        borderLeft: 3,
        borderColor: 'error.main'
      }
    }
  })), /*#__PURE__*/React.createElement(MeterTotals, {
    isUpload: true,
    rows: rows,
    bill: (_utility$bill8 = utility === null || utility === void 0 ? void 0 : utility.bill) !== null && _utility$bill8 !== void 0 ? _utility$bill8 : {}
  }), renderInvalidReadsWarning(parsedRows), renderUsageWarning(parsedMeteredUnits), renderUsageUnitNotes(parsedRows, parsedMeteredUnits), !isBillRemoved && /*#__PURE__*/React.createElement("div", {
    className: classes.saveButtonWrapper
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    onClick: handleSave,
    disabled: isMappingBusy || isUploadSaving || store.allocations.postSubmeterBillingAllocationsSaveAsync.inProgress
  }, isUploadSaving && store.allocations.postSubmeterBillingAllocationsSaveAsync.inProgress ? 'Saving...' : 'Add bill to meter'))));
});